// Boston's Public Garden look (the registry's `looks`; boston.js, the Charles from the Cambridge bank, is the city's
// first), "Swan boats after the rain". A mild night in late May, about half past nine, just after a spring shower: the
// lawns and paths shine under the lamps, the lagoon lies still and doubles the bridge's lamps, the last tulips stand in
// their beds and the weeping willows trail over the water. The Swan Boats are tied up at their landing for the night.
// Where we stand: on the raised north-east corner of the lagoon's south lobe (42.3539 N, 71.0693 W), a few metres
// south-east of the Swan Boat landing, looking west across the water (the view spans bearings 200 to 340). Measured on
// the map (OpenStreetMap) and checked against photos, left to right as seen from there:
//   - over the far (south-west) shore's trees, the Back Bay: the Berkeley Building (550 m, bearing 215.6) with its
//     weather beacon, and right in front of it the brownstone steeple of Arlington Street Church (246 m, 215.2); then
//     200 Clarendon, the old John Hancock Tower (660-750 m, 219-226, its long north-east face nearly square on to us,
//     19-20 degrees tall), with the tops of 222 Berkeley and 500 Boylston (9.5 degrees) in front of its foot;
//   - the Taj (15 Arlington, 204-248 m, 235-244) and Carlton House (2 Commonwealth Avenue, 199-249 m, 247-254), the two
//     17-storey blocks on Arlington Street. The Prudential (235.4, 1.3 km) stands right behind the Taj from here: hidden;
//   - George Washington on his horse at the Arlington Street gate (138 m, 268.8), small, at the far end of the central
//     path whose tulip beds run from him to the bridge;
//   - the lagoon footbridge (1867) across the waist between the two lobes (49-75 m, 286-316), seen at about 48 degrees
//     from its axis: three spans, two granite piers each carrying two granite towers with globe lamps, end posts with
//     lamps at the abutments, the pale blue iron chains and lattice railing;
//   - the Swan Boat landing: the green-roofed pavilion on its float (31-40 m, 295-302) right under the bridge's main
//     span, and four of the six boats tied up west of it (41-46 m, 278-296; the other two lie behind the pavilion);
//   - the east bank: the bridge's steps, willows, a lamp post, and a Boston cream pie cart (Dora's, invented) on the
//     path by the landing with its lit sign on the canopy (the city's billboard).
// What the brief hoped for but this spot can't show truthfully: Make Way for Ducklings stands by the Charles and Beacon
// corner, 190 m behind the viewer's right shoulder (a mallard hen and her eight ducklings paddle by instead); the State
// House's gold dome is behind the viewer (bearing 40) and, in May, under the leaves of the Common's trees anyway; the
// Prudential is behind the Taj. The Charles, Fenway and the harbour are far out of sight.
// The panorama is compressed between the landmarks (bx below), never reordered: everything is drawn at its true size for
// its distance (S px a degree, eye 3 m over the water), the bridge, the pavilion and the boats projected from the map.
// render(t, { crowd }) thins the people (see CROWD); crowd 0 draws nobody, and the cart stands unattended.
//
// The loop's timeline (seconds):
//   2-16      a couple strolls by on the near path, right to left
//   12-52     the garden's two swans glide across the lagoon to the landing (back again 128-168)
//   20-46     an airliner crosses high over the Back Bay (again 176-202)
//   34-44     a couple walks onto the bridge and stops at mid-span; they stay, watch the swans and the shower
//   60-118    BIG: the last shower passes over: the cloud bank slides in and hides the moon (60-74), the first rings
//             (72), the shower (78-102) rings the whole lagoon, umbrellas open, the photographer covers the camera, the
//             vendor drops the canopy's flap; it thins (102-112) and clears (112-118); the willows drip harder after
//   120-140   a dog walker comes by; the dog stops at the curb to watch the mallards (127-133)
//   140-170   BIG: Arlington Street Church's bells: the quarter chime (140-145), then ten strikes (146-168.5); the
//             couple on the bridge turn to listen, the reader looks up
//   150-158   the couple on the bridge kiss as the strikes go on; they walk off the east end (160-172), come down the
//             bank's path towards us (172-186) and away along the near path (186-202)
//   172-202   a mallard hen and her eight ducklings paddle in a line from the landing along the near curb; the kid
//             crouches and points
//   186-226   BIG: a breeze: cat's paws race over the far water (186-192), the willows sway, petals blow off the hawthorn
//             by the landing onto the water, the boats tug at their lines and the flag streams (192-212), then it dies
//             down and the petals float and drift away (212-238)
//   205-215   a late jogger runs by, left to right
//   226-238   a walker with a phone light follows the far shore path
//   all loop  clouds drift over the stars and the moon, mist breathes over the water, the willows drip and ring the
//             water, frogs plop at the far edge, a fish rises, bats hawk over the lagoon, moths circle the lamps, the
//             Hancock's glass mirrors the clouds, the Berkeley beacon burns steady blue (clear ahead: it forecasts the
//             weather on show), aircraft lamps pulse, windows switch on and off, the pavilion's flag stirs, the boats
//             bob, mallards dabble, the night heron on the pier strikes every 80 s, a rabbit grazes the east lawn, the
//             cart sells a slice of cream pie every 24 s and the reader turns a page every 20 s
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, streak, glyph, text, makePitch, gradeAd, FONT3 } from '../engine/core.js';
import { WEATHERS, weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Projection: the map, seen from the bank ================= */
// Local metres: x east, y north of 42.3540 N, 71.0700 W. The viewer's eye is EYE m over the water; a thing h m over the
// water at distance d sits S * atan((h - EYE) / d) degrees above row H0. Bearings map to columns through the knots.
const H0 = 148;                                     // the eye's row: the horizon
const S = 5;                                        // px a degree, for every thing's own size
const EYE = 3;                                      // the eye, metres over the water (the bank stands a little high)
const VP = [60, -14];                               // the viewpoint
const DEG = 180 / Math.PI;
const ACCENT = '#44bb11';                           // the Green Line's green (Arlington station is at the garden's corner)
const KNOTS = [[200, -36], [212.8, 14], [215.4, 26], [222.7, 62], [225.6, 76], [240.1, 118], [250, 152], [268.8, 196],
  [286.3, 240], [315.8, 388], [340, 490]];
function bx(b) {
  if (b <= KNOTS[0][0]) return KNOTS[0][1];
  for (let i = 1; i < KNOTS.length; i++) if (b <= KNOTS[i][0]) {
    const [b0, x0] = KNOTS[i - 1], [b1, x1] = KNOTS[i];
    return x0 + (x1 - x0) * (b - b0) / (b1 - b0);
  }
  return KNOTS[KNOTS.length - 1][1];
}
/** screen [column, row, distance] of a point h metres over the water at local (x, y) */
function pr(x, y, h) {
  const dx = x - VP[0], dy = y - VP[1], d = Math.hypot(dx, dy), b = (Math.atan2(dx, dy) * DEG + 360) % 360;
  return [bx(b), H0 - S * Math.atan2(h - EYE, d) * DEG, d];
}
/** rows of something h metres tall at d metres */
const up = (h, d) => S * Math.atan2(h, d) * DEG;
/** the row of the water's surface at d metres */
const wlAt = d => H0 + up(EYE, d);

/* ================= Layout (screen) ================= */
const CURB = 196;                                   // the near curb: the lagoon ends above this row
const PATH_Y = 203;                                 // feet of the people walking the near path
const LAWN = 212;                                   // where the path gives way to the lawn
// the far shore's waterline per column (the south lobe's west and south shores, measured along rays on the map), then
// under the bridge the water runs on into the north lobe, then the north lobe's strip behind the east bank
const FAR_KEYS = [[0, 154.6], [26, 155], [50, 156], [62, 158.5], [76, 159.3], [90, 162.4], [118, 162.5], [152, 161.8],
  [175, 160.6], [196, 160.4], [240, 161.1], [244, 156], [392, 156], [396, 153], [480, 153]];
const keyAt = (keys, x) => {
  if (x <= keys[0][0]) return keys[0][1];
  for (let i = 1; i < keys.length; i++) if (x <= keys[i][0]) { const [a, p] = keys[i - 1], [b, q] = keys[i]; return p + (q - p) * (x - a) / (b - a); }
  return keys[keys.length - 1][1];
};
const FAR = new Float32Array(W);
for (let x = 0; x < W; x++) FAR[x] = keyAt(FAR_KEYS, x);
// the bank strip between the south lobe and the landing: its near edge (water below it again down to the curb) and
// far edge (the waist's water above it), then the waist's east shore from the pavilion to the bridge's east end (a
// few rows of water stay open under the landing's float, crossed by its gangway)
const BANK_NEAR = [[246, 196], [250, 191], [284, 193], [300, 197]];
const BANK_FAR = [[246, 196], [258, 178.5], [284, 177.2]];
const SHORE_E = [[300, 177.2], [318, 177], [324, 173.5], [334, 172], [346, 164.5], [358, 162.5], [372, 161], [392, 159.8]];
// the bridge: its axis from the west abutment to the east one, the near face (towards us) and the heights (metres)
const BR = { w: [-12, 7], e: [26, 21], half: 2.3, tower: 2.6, deckLo: 2.0, deck: 2.5, rail: 3.5, towerTop: 5.2, lampTop: 6.4,
  post: 3.8, postLamp: 5.9 };
const BR_LEN = Math.hypot(BR.e[0] - BR.w[0], BR.e[1] - BR.w[1]);
const BR_U = [(BR.e[0] - BR.w[0]) / BR_LEN, (BR.e[1] - BR.w[1]) / BR_LEN];   // along the bridge, west to east
const BR_N = [BR_U[1], -BR_U[0]];                   // across it, towards our (south) side
// spans along the axis (m from the west end): abutment 1, side 9.35, pier 2.4, main 15, pier 2.4, side 9.35, abutment 1
const PIERS = [[10.35, 12.75], [27.75, 30.15]];
const PIER_C = PIERS.map(([a, b]) => (a + b) / 2);
/** local point s metres along the bridge from the west end, o metres to our side of its axis */
const brPt = (s, o) => [BR.w[0] + BR_U[0] * s + BR_N[0] * o, BR.w[1] + BR_U[1] * s + BR_N[1] * o];
const brAt = (s, o, h) => { const [x, y] = brPt(s, o); return pr(x, y, h); };
// the Swan Boat landing: the pavilion on its float, the boats tied up west of it (two rafted pairs, sterns north)
const PAV = { x0: 285, x1: 316, roof: 145, eave: 149, float: 171 };
const BOATS = [                                     // [local x, local y of the boat's middle]; outer boats sit 3.5 m west
  { lx: 21, ly: 0, outer: false }, { lx: 17.5, ly: 0.6, outer: true }, { lx: 21, ly: -10.2, outer: false }, { lx: 17.5, ly: -9.6, outer: true }];
// the billboard: Dora's sign on the cream pie cart's canopy, on the path by the landing
const BB = { x: 404, y: 120, w: 46, h: 20 };
const CART = { x0: 410, x1: 444, top: 160, counter: 180, base: 199 };
// near things
const LAMP_N = { x: 104, y: 136 };                  // the garden lamp by the bench (the globe's centre)
const LAMP_E = { x: 352, y: 150 };                  // a lamp on the east bank by the landing path
const BENCH = { x: 52, y: 206 };
const WILLOW_R = { x: 470, base: 210 };             // the near willow at the right edge
const HAWTHORN = { x: 361, y: 178 };                // the hawthorn in blossom by the landing (the breeze's petals), clear of the bank path

/* ================= The loop's timeline ================= */
const STROLL = { t0: 2, t1: 16 };
const SWANS = { go: [12, 52], back: [128, 168] };
const JETS_HI = [{ t0: 20, t1: 46 }, { t0: 176, t1: 202 }];
const BRIDGE_PAIR = { on: 34, stop: 44, kiss: [150, 158], off: 160, gone: 172, down: 186, away: 202 };
const SHOWER = { t0: 60, cover: 74, rain0: 72, full: 78, thin: 102, clear: 112, t1: 118 };
const DOGW = { t0: 120, t1: 140, stop: [127, 133] };
const BELLS = { chime: [140, 141.25, 142.5, 143.75], strike0: 146, n: 10, gap: 2.5 };
const DUCKLINGS = { t0: 172, t1: 202 };
const BREEZE = { t0: 186, gust: 192, ease: 212, t1: 226, petals: 238 };
const JOG = { t0: 205, t1: 215 };
const FARWALK = { t0: 226, t1: 238 };
const HERON = [30, 110, 190];
const STRIKES = Array.from({ length: BELLS.n }, (_, i) => BELLS.strike0 + i * BELLS.gap);
// The people: about half of a cast of 16 by default. render(t, { crowd }) shows those whose rank is under crowd; rank 0
// (the reader, the vendor, the couple on the bridge) stays while anybody does; crowd 0 draws nobody and takes the
// umbrellas, the tripod, the dog and the reader's book along. The swans, ducks, heron, rabbit and the boats stay.
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);
const RANK = { reader: 0, vendor: 0, pair: 0, kid: 0.12, photo: 0.25, queue: 0.3, dog: 0.38, jog: 0.45, stroll: 0.55,
  farwalk: 0.62, queue2: 0.7, far2: 0.8 };

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
/** piecewise-linear script: keys [[t, x], ...] sorted by t; the position at t and whether it moves */
function along(keys, t) {
  if (t <= keys[0][0]) return { x: keys[0][1], moving: false };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i];
    return { x: lerp(a[1], b[1], (t - a[0]) / (b[0] - a[0])), moving: a[1] !== b[1] };
  }
  return { x: keys[keys.length - 1][1], moving: false };
}
const inWin = (t, a, b) => t >= a && t < b;
/** the red aircraft lamps pulse slowly, all together: 0..1 */
const beacon = t => smooth(0.2, 0.85, 0.5 + 0.5 * wave(t, 80));
/** how much the last shower is falling, 0..1 (the city's own weather) */
const showerAt = t => smooth(SHOWER.rain0, SHOWER.full, t) * (1 - smooth(SHOWER.thin, SHOWER.clear, t));
/** how thick the passing cloud bank is over the scene, 0..1 */
const coverAt = t => smooth(SHOWER.t0, SHOWER.cover, t) * (1 - smooth(SHOWER.clear - 4, SHOWER.t1 + 6, t));
/** the breeze, 0..1: it builds from the left, gusts, then dies down */
const breezeAt = t => smooth(BREEZE.t0, BREEZE.gust, t) * (1 - smooth(BREEZE.ease, BREEZE.t1, t));
/** the willows' sway (px at the frond tips): a slow breathing all loop, much more in the breeze */
const swayAt = (t, ph) => 0.6 * wave(t, 20, ph) + 0.3 * wave(t, 52, ph * 2) + breezeAt(t) * (2.2 + 1.2 * wave(t, 96, ph));

/* ================= The sky: a rinsed night, clouds breaking up, the moon, the Back Bay's glow ================= */
// The shower has just moved off east: the sky is a deep, clean blue, warm low in the south-west over the Back Bay's
// lights, with broken clouds drifting off to the north-east and a waxing gibbous moon between them.
const SKY = ['#05081a', '#070b20', '#0a0f27', '#0d132e', '#111836', '#151d3e', '#1a2245', '#20284c', '#272e52', '#2f3457',
  '#383a5b', '#423f5d', '#4c445e', '#574a5e'];
const skyV = (x, y) => Math.pow(y / H0, 1.7) * 0.6 + 0.26 * Math.exp(-(((x - 70) / 140) ** 2)) * smooth(30, H0, y)
  + 0.07 * smooth(80, H0, y);
function renderSky() {
  const p = pix(W, H0 + 16);
  for (let y = 0; y < H0 + 16; y++) for (let x = 0; x < W; x++) p.set(x, y, pick(SKY, skyV(x, Math.min(y, H0)) + (vnoise(x / 60, y / 9, 3) - 0.5) * 0.03, x, y));
  return p.done();
}
const hazeAt = (x, y) => SKY[Math.min(SKY.length - 1, Math.round(clamp(skyV(x, y), 0, 1) * (SKY.length - 1)))];
const STARS = [];
{
  const r = rng(517);
  while (STARS.length < 46) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 78);
    if (skyV(x, y) > 0.22 || (x > 160 && x < 190 && y > 18 && y < 42)) continue;
    STARS.push({ x, y, k: 9 + Math.floor(r() * 31), ph: r(), b: 0.3 + r() * r() * 0.7, c: r() < 0.25 ? '#ffe8c8' : r() < 0.5 ? '#cfdcff' : '#eef0ff' });
  }
}
const MOON = { x: 174, y: 30, r: 5 };
function renderMoon() {                             // waxing gibbous, its dark limb to the left
  const s = MOON.r * 2 + 3, p = pix(s, s), c = MOON.r + 1;
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    const dx = x - c, dy = y - c, d = Math.hypot(dx, dy);
    if (d > MOON.r + 0.3) continue;
    const lit = dx > -MOON.r * 0.45 * Math.sqrt(Math.max(0, 1 - (dy / MOON.r) ** 2));   // the terminator's curve
    if (!lit) continue;
    const mare = vnoise(x / 2, y / 2, 41) > 0.62;
    p.set(x, y, d > MOON.r - 0.7 ? '#e8e2cc' : mare ? '#cfc8b4' : '#fbf6e2');
  }
  return p.done();
}
/* broken clouds, tileable (period W): dark bodies with paler, city-lit bellies and soft dithered edges */
function makeClouds(seed, h, n, rx, ry, cols, a) {
  const r = rng(seed), blobs = [];
  for (let i = 0; i < n; i++) {
    const cx = r() * W, cy = ry + r() * (h - ry * 2), k = 2 + Math.floor(r() * 4);
    for (let j = 0; j < k; j++) blobs.push({ x: cx + (r() - 0.5) * rx * 1.6, y: cy + (r() - 0.5) * ry * 0.8, rx: rx * (0.5 + r() * 0.6), ry: ry * (0.45 + r() * 0.5) });
  }
  const p = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0, lo = 0;
    for (const b of blobs) {
      let dx = x - b.x; dx -= Math.round(dx / W) * W;
      const q = 1 - (dx / b.rx) ** 2 - ((y - b.y) / b.ry) ** 2;
      if (q > v) { v = q; lo = (y - b.y) / b.ry; }
    }
    v *= 0.7 + 0.6 * vnoise(x / 7, y / 4, seed, Math.round(W / 7));
    if (v < 0.1 + bayer(x, y) * 0.35) continue;
    const col = lo > 0.45 && v < 0.7 ? cols[2] : lo > 0.1 ? cols[1] : cols[0];
    p.set(x, y, col, Math.round(a * Math.min(1, 0.5 + v) * 255));
  }
  return p.done();
}
/* the shower's cloud bank: a heavy, low deck, darker in its middle, with ragged edges both sides */
function makeDeck() {
  const w = W + 160, p = pix(w, 124);
  for (let y = 0; y < 124; y++) for (let x = 0; x < w; x++) {
    const edge = Math.min(x, w - 1 - x) / 80, n = vnoise(x / 18, y / 6, 77) * 0.6 + vnoise(x / 6, y / 3, 78) * 0.4;
    const v = Math.min(1, edge) * (0.55 + 0.5 * n) - (y > 100 ? (y - 100) / 40 : 0);
    if (v < 0.2 + bayer(x, y) * 0.3) continue;
    p.set(x, y, n > 0.66 && y > 70 ? '#2a2840' : n > 0.42 ? '#1e1f34' : '#16192c', Math.round(Math.min(1, v + 0.2) * 235));
  }
  return p.done();
}

/* ================= Windows that switch on and off ================= */
const WINS = [];
function addTog(r, x, y, w, h, col) {
  WINS.push({ x, y, w, h, c: col, k: 2 + Math.floor(r() * 5), ph: r(), duty: 0.3 + r() * 0.45 });
}
const WARMW = ['#ffd890', '#f8c070', '#ffe6b0', '#f0b060'], COOLW = ['#e4ecf8', '#c8d8ec', '#d8e2f0'];

/* ================= The Back Bay over the trees ================= */
const LIME = ['#2a2a34', '#3c3a44', '#55505a', '#716a70', '#8e8686', '#aaa29e'];   // the Berkeley Building's limestone
const BROWN = ['#2a1a18', '#3e2620', '#58362a', '#744a34', '#946044'];                // the church's brownstone, floodlit
const BRICK = ['#1e1416', '#2e1c1c', '#422822', '#5a3628', '#744630'];
const CONC = ['#24242c', '#33333c', '#46454e', '#5c5a62', '#74717a'];
const HAN = { x0: 42, x1: 81, notch: 77, top: 52 };     // 200 Clarendon: its long north-east face, then the notched north-west end
const BER = { x: 27, x0: 14, x1: 41, top: 80, peak: 73, mast: 59 };   // the Berkeley Building and its beacon
const STEEPLE = { x: 25, tip: 85 };
const TAJ = { x0: 94, x1: 140, top: 73 }, CARL = { x0: 134, x1: 171, top: 71 };
const TREE_TOP = new Int16Array(W);
const FAR_STRANDS = [];                            // the far willows' strands, which sway (per frame)                // the tree line's top row per column (filled by paintTrees)
function paintBerkeley(p, r) {
  const { x0, x1, top, peak, mast } = BER;
  for (let x = x0; x < x1; x++) for (let y = top; y < 124; y++) {
    const u = (x - x0) / (x1 - x0 - 1), set = x < x0 + 3 || x > x1 - 4 ? (y < top + 3 ? -1 : 0) : 0;
    if (set < 0) continue;
    let col = pick(LIME, 0.62 - u * 0.42 + (y === top ? 0.25 : 0) + (x === x0 ? 0.12 : 0), x, y, 0.6);
    if (y > top + 2 && (y - top) % 2 === 1 && x > x0 && x < x1 - 1) { const v = r(); if (v < 0.24) col = mixHex(WARMW[Math.floor(r() * 4)], col, 0.3); else if (v < 0.3) addTog(r, x, y, 1, 1, mixHex(WARMW[2], col, 0.3)); }
    p.set(x, y, mixHex(col, hazeAt(x, y), 0.3));
  }
  for (let j = 0; j < top - peak; j++) {                                   // the hipped roof, lit from below
    const hw = Math.round((x1 - x0 - 6) / 2 * (1 - j / (top - peak)));
    for (let x = BER.x - hw; x <= BER.x + hw; x++) p.set(x, top - 1 - j, x <= BER.x - hw + 1 ? '#8a8e84' : x < BER.x ? '#6a7268' : '#4a524a');
  }
  for (let y = mast; y < peak; y++) { p.set(BER.x, y, '#3a3a44'); if (y > peak - 5) p.set(BER.x - 1, y, '#2a2a34'); }   // the beacon's mast (lit per frame)
}
function paintSteeple(p) {                                                    // Arlington Street Church, floodlit brownstone
  const { x, tip } = STEEPLE, mine = new Set(), set = (i, y, col) => { p.set(i, y, col); mine.add(y * W + i); };
  const stage = (y0, y1, hw, col) => { for (let y = y0; y < y1; y++) for (let i = -hw; i <= hw; i++) set(x + i, y, pick(BROWN, col - (i + hw) / (2 * hw + 1) * 0.35 + (y === y0 ? 0.2 : 0), x + i, y)); };
  for (let y = tip; y < tip + 10; y++) { const hw = Math.floor((y - tip) / 4); for (let i = -hw; i <= hw; i++) set(x + i, y, i < 0 ? '#a07a5a' : i === 0 ? '#80604a' : '#5a4232'); }
  stage(tip + 10, tip + 16, 2, 0.75);                                         // the lantern, its columns
  for (const i of [-1, 1]) for (let y = tip + 11; y < tip + 15; y++) set(x + i, y, '#2a1810');
  stage(tip + 16, tip + 18, 3, 0.85);                                         // a cornice
  stage(tip + 18, tip + 27, 3, 0.7);                                          // the belfry, its louvred openings
  for (let y = tip + 20; y < tip + 25; y++) { set(x - 1, y, '#1a100c'); set(x + 1, y, '#1a100c'); }
  // the square tower under the belfry, down into the far shore's trees (they hide the church itself): a string course,
  // a tall round-headed window, a second course, the plain shaft brighter low down where the floodlights stand
  stage(tip + 27, tip + 29, 3, 0.82);
  for (let y = tip + 29; y < 136; y++) for (let i = -3; i <= 3; i++) set(x + i, y, pick(BROWN, 0.6 + (y - tip - 29) * 0.006 - (i + 3) / 7 * 0.35, x + i, y));
  for (let y = tip + 31; y < tip + 37; y++) set(x, y, y === tip + 31 ? '#3a2418' : '#1a100c');
  stage(tip + 39, tip + 40, 3, 0.7);
  set(x, tip - 1, '#c8a878');                                                 // the finial
  for (let i = WINS.length - 1; i >= 0; i--) if (mine.has(WINS[i].y * W + WINS[i].x)) WINS.splice(i, 1);   // no lit window over the stone
}
// 200 Clarendon at night: a sheer slab of blue mirror glass that holds the sky, darker high up, the city's warm glow
// rising up its foot; the mullions' faint vertical grain. The notched north-west end is in shade. No windows to speak
// of: the glass is the building, and the clouds drifting over the Back Bay drift across it too (drawHancockClouds).
const HAN_GLASS = ['#0a0f22', '#0e152c', '#141c36', '#1b2442', '#232d4e', '#2c375a', '#3a4266'];
function paintHancock(p, r) {
  const { x0, x1, notch, top } = HAN;
  for (let x = x0; x < x1; x++) for (let y = top; y < 124; y++) {
    const end = x >= notch - 2, u = (y - top) / 70;
    let v = end ? 0.22 - (x - notch + 2) * 0.04 + u * 0.1 : 0.3 + 0.42 * u * u - (x - x0) * 0.004 + (vnoise(x / 6, y / 14, 91) - 0.5) * 0.1 + ((x - x0) % 3 === 0 ? 0.04 : 0);
    if (x === notch) v = 0.0;                                                  // the notch: a deep shadowed slot
    if (x === x0) v += 0.22;
    let col = pick(HAN_GLASS, v, x, y, 0.5);
    if (y === top) col = end ? '#3a4466' : '#7a88aa';
    p.set(x, y, mixHex(col, hazeAt(x, y), 0.1));
  }
}
// the clouds the glass holds: soft, dithered streaks a shade paler than the glass, tileable across (HAN_TW), drawn per
// frame through a mask of the long face that stops at 222 Berkeley's roof (makeHanMask)
const HAN_TW = 80, HAN_H = 50;
function makeHanClouds() {
  const p = pix(HAN_TW, HAN_H);
  for (let y = 0; y < HAN_H; y++) for (let x = 0; x < HAN_TW; x++) {
    const d = vnoise(x / 16, y / 4, 95, HAN_TW / 16) * 0.7 + vnoise(x / 8, y / 2, 96, HAN_TW / 8) * 0.3 - y * 0.002;
    const th = bayer(x, y) * 0.16;
    if (d > 0.68 + th) p.set(x, y, '#3a3c5c');
    else if (d > 0.56 + th) p.set(x, y, '#2c3150');
  }
  return p.done();
}
const boylTop = x => Math.round(100 - (x >= 70 && x < 90 ? Math.max(0, 4 - Math.abs(x - 79.5) * 0.4) : 0));
function makeHanMask() {
  const { x0, notch, top } = HAN, p = pix(W, HAN_H);
  for (let x = x0 + 1; x < notch - 2; x++) for (let y = top + 2; y < top + 2 + HAN_H; y++) if (x < 50 || y < boylTop(x) - 1) p.set(x, y - top - 2, '#ffffff');
  return p.done();
}
function paintBoylston(p, r) {                                                // 222 Berkeley and 500 Boylston, pale stone, their tops over the trees
  for (let x = 50; x < 93; x++) {
    const top = boylTop(x);
    for (let y = top; y < 124; y++) {
      let col = pick(CONC, 0.55 + (y === top ? 0.3 : 0) + (x === 50 || x === 70 ? 0.15 : 0) - (x > 86 ? 0.15 : 0), x, y, 0.6);
      if (y > top + 2 && y % 2 === 0 && x % 2 === 1 && r() < 0.5) col = r() < 0.7 ? WARMW[Math.floor(r() * 4)] : COOLW[0];
      p.set(x, y, mixHex(col, hazeAt(x, y), 0.25));
    }
  }
}
// the Taj (1927): dark red brick in pairs of sash windows, a limestone band under its set-back top floors; hotel rooms lit
function paintTaj(p, r) {
  const { x0, x1, top } = TAJ;
  for (let x = x0; x < x1; x++) {
    const set = x < x0 + 14 ? 3 : 0;                                           // the top floors step back on the south side
    for (let y = top + set; y < 124; y++) {
      const fl = (y - top) % 3, u = (x - x0) / (x1 - x0), band = y >= top + 6 && y < top + 8;
      let col = band ? pick(["#5a5450", "#7a726a", "#9a9084"], 0.6 - u * 0.4, x, y) : pick(BRICK, 0.42 - u * 0.2 + (fl === 0 ? 0.06 : 0) + (x === x0 ? 0.25 : 0), x, y, 0.6);
      if (y === top + set) col = "#8a8076";
      else if (!band && fl === 1 && (x - x0) % 4 !== 3 && x > x0 && x < x1 - 1 && y > top + set + 1) {
        const v = r();
        if (v < 0.22) col = mixHex(WARMW[Math.floor(r() * 4)], col, 0.25); else if (v < 0.3) addTog(r, x, y, 1, 1, mixHex(WARMW[Math.floor(r() * 4)], col, 0.25));
        else if (v < 0.5) col = "#1a1214";
      }
      p.set(x, y, mixHex(col, hazeAt(x, y), 0.2));
    }
  }
  for (let x = x0 + 18; x < x0 + 24; x++) for (let y = top - 3; y < top; y++) p.set(x, y, mixHex(BRICK[2], hazeAt(x, y), 0.25));   // the roof house
}
// Carlton House: a plainer block of pale concrete, its floors marked by balcony bands, the rooms lit here and there
function paintCarlton(p, r) {
  const { x0, x1, top } = CARL;
  for (let x = x0; x < x1; x++) for (let y = top; y < 124; y++) {
    const fl = (y - top) % 3, u = (x - x0) / (x1 - x0);
    let col = pick(CONC, (fl === 0 ? 0.62 : 0.3) - u * 0.25 + (x === x0 ? 0.15 : 0), x, y, 0.6);
    if (y < top + 2) col = y === top ? "#7a7680" : "#4a4852";
    else if (fl === 1 && x > x0 && x < x1 - 1) { const v = r(); if (v < 0.18) col = mixHex(r() < 0.6 ? WARMW[1] : COOLW[1], col, 0.3); else if (v < 0.25) addTog(r, x, y, 1, 1, mixHex(WARMW[0], col, 0.3)); }
    p.set(x, y, mixHex(col, hazeAt(x, y), 0.24));
  }
}

/* ================= The garden's trees, the far shore, George Washington ================= */
const TREE = ['#04070a', '#070c0d', '#0a1210', '#0f1814', '#152018', '#1c2a1c', '#263620', '#324426'];
const WILLOWC = ['#0a110c', '#101a12', '#172416', '#20321a', '#2c401e', '#3c5224', '#506628'];
const FAR_LAMPS = [[12, 151], [88, 156], [128, 157], [168, 155], [212, 154], [236, 153], [404, 150], [448, 149]];
function paintTrees(p) {
  // the tree line: elms along Arlington and Boylston Streets and in the garden, lower over the lawns by the statue,
  // humped where the willows on the shores stand nearer
  for (let x = 0; x < W; x++) {
    const base = 116 + 6 * Math.sin(x * 0.021 + 1) - (x > 170 && x < 230 ? -4 : 0) - (x > 380 ? 6 : 0);
    TREE_TOP[x] = Math.round(base + (vnoise(x / 9, 1, 401) - 0.5) * 12 + (vnoise(x / 3, 2, 402) - 0.5) * 4);
  }
  for (let x = 0; x < W; x++) for (let y = TREE_TOP[x]; y < Math.ceil(FAR[x]) + 1; y++) {
    const n = vnoise(x / 4, y / 4, 403), m = vnoise(x / 11, y / 9, 404), deep = (y - TREE_TOP[x]) / 30;
    let v = 0.22 + (n - 0.5) * 0.4 + (m - 0.5) * 0.25 - deep * 0.15 - (y === TREE_TOP[x] ? 0.1 : 0);
    let col = pick(TREE, v, x, y, 0.8);
    if (y < TREE_TOP[x] + 2) col = mixHex(col, hazeAt(x, y), 0.35);
    p.set(x, y, col);
  }
  // willows on the far shores and by the bridge: ragged crowns pouring down in strands to the water, darkest inside,
  // a lamp nearby picking out their edges in yellow-green
  for (const [cx, top, rw, base, lit] of [[8, 130, 16, 154, 0.12], [70, 136, 13, 158, 0.08], [120, 138, 11, 162, 0.18], [178, 134, 14, 160, 0.14],
    [226, 130, 16, 160, 0.3], [252, 134, 11, 160, 0.22], [404, 126, 19, 157, 0.3], [440, 132, 15, 155, 0.16]]) {
    for (let x = cx - rw - 2; x <= cx + rw + 2; x++) {
      const u = (x - cx) / rw, crown = top + Math.round(u * u * 9 + (vnoise(x / 3, 3, 406 + cx) - 0.5) * 5);
      const hang = base - Math.round(hash(x, cx, 407) * (Math.abs(u) > 0.8 ? 10 : 4));
      if (Math.abs(u) > 1.15) continue;
      for (let y = crown; y < hang; y++) {
        const strand = vnoise(x * 0.9, y / 10, 405 + cx), edge = Math.abs(u) > 0.7 ? (Math.abs(u) - 0.7) * 1.4 : 0;
        const v = 0.18 + lit * Math.max(0, 1 - Math.abs(u + 0.3)) * 0.8 + (strand - 0.5) * 0.45 - (y - crown) / (base - crown) * 0.15 - edge * 0.2;
        if (Math.abs(u) > 0.95 && hash(x, y, cx + 1) < 0.5) continue;
        p.set(x, y, pick(WILLOWC, v, x, y, 0.8));
      }
      if (hash(x, cx, 409) < 0.4) FAR_STRANDS.push({ x, y0: Math.round(lerp(crown, hang, 0.45)), y1: hang - 1, ph: hash(x, cx, 410), col: pick(WILLOWC, 0.3 + lit * 0.8 * Math.max(0, 1 - Math.abs(u + 0.3)), x, hang) });
    }
  }
  for (const [x, y] of FAR_LAMPS) for (let k = 1; k < 7; k++) p.set(x, y + k, '#0a0c10');   // the lamps' posts
}
// the far shore: a granite edge catching the lamps, the tulip beds along the central path, the duck house's float
function paintShore(p) {
  for (let x = 0; x < 244; x++) {
    const y = Math.ceil(FAR[x]);
    p.set(x, y - 1, pick(['#1a2018', '#2a2e2a', '#46484a'], 0.4 + 0.3 * Math.sin(x * 0.7), x, y));
    p.set(x, y, '#08090e');
  }
  for (let x = 198; x < 238; x++) for (let y = 152; y < 156; y++) {          // the tulip beds flanking the path to the bridge
    if (hash(x, y, 61) < 0.32) p.set(x, y, mixHex(['#a83a3a', '#c8a03a', '#b85a7a', '#e8e0c8'][Math.floor(hash(x, y, 62) * 4)], '#0a0e1c', 0.55 - (x - 198) * 0.006));
  }
  for (let x = 198; x < 240; x++) p.set(x, 156, '#2a2a32');                    // the path's pale gravel
}
// George Washington on his horse at the Arlington Street gate, seen from behind his left: bronze on a granite pedestal
const WASH = { x: 196, base: 154 };
const WASH_ROWS = [
  '.....b......', '.....bb.....', '....bbbb....', '....bBbb....', '.bb.bbbbb...', 'bBbbbbbbbbb.', '.bbbbbbbbbbb',
  '..bbbbbbbb.b', '..b.b..b.b..', '.b..b..b..b.', '.GGGGGGGGGG.', '..gggggggG..', '..gggggggG..', '..gggggggG..',
  '..gggggggG..', '..gggggggG..', '..gggggggG..', '..gggggggG..', '.GGGGGGGGGG.', 'GGGGGGGGGGGG'];
function paintStatue(p) {
  const key = { b: '#3a4234', B: '#7a846a', g: '#2a2a32', G: '#1e1e26' };
  WASH_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) p.set(WASH.x - 6 + i, WASH.base - WASH_ROWS.length + j, key[row[i]]); });
  const y0 = WASH.base - WASH_ROWS.length;                                    // the path lamps catch the bronze's top edges
  for (const [i, j] of [[5, 0], [5, 1], [4, 2], [1, 4], [2, 4], [0, 5], [6, 4], [7, 4], [8, 4], [9, 5], [10, 6]]) p.set(WASH.x - 6 + i, y0 + j, '#8a9478');
  for (let i = 1; i < 11; i++) p.set(WASH.x - 6 + i, y0 + 10, '#5a5a62');
  for (let i = -9; i <= 9; i++) p.set(WASH.x + i, WASH.base, mixHex('#3a3a40', '#0a0e14', Math.abs(i) / 10));   // the gravel round his plinth
}

/* ================= The lagoon footbridge (1867) ================= */
// Projected from the map: 40.5 m between the abutments, three spans (side, main of about 15 m, side) on two rusticated
// granite piers. Each pier carries two granite towers, one either side of the deck, capped by a cornice and a slender
// pale-blue post with a white globe lamp; the abutments have shorter end posts with lamps. The pale-blue iron chains
// swoop from tower top to tower top over the main span and down to the end posts over the side spans (decorative since
// the 1921 girders), above a lattice railing. Seen from the south-east, 49 to 75 m off.
const GRAN = ['#16161e', '#22222a', '#302f37', '#423f46', '#57525a', '#706a6a', '#908680', '#b0a496'];
const IRON = { lit: '#a8c4d2', mid: '#7e98a8', dark: '#4a5c68', post: '#88a2b2' };
/** fill the convex polygon of screen points pts with col(x, y) (build time) */
function fillPoly(p, pts, col) {
  const ys = pts.map(q => q[1]), y0 = Math.ceil(Math.min(...ys) - 0.5), y1 = Math.floor(Math.max(...ys) - 0.5);
  for (let y = y0; y <= y1; y++) {
    const yc = y + 0.5, xs = [];
    for (let i = 0; i < pts.length; i++) {
      const [ax, ay] = pts[i], [bx2, by] = pts[(i + 1) % pts.length];
      if ((ay <= yc && by > yc) || (by <= yc && ay > yc)) xs.push(ax + (yc - ay) * (bx2 - ax) / (by - ay));
    }
    if (xs.length < 2) continue;
    const xa = Math.ceil(Math.min(...xs) - 0.5), xb = Math.floor(Math.max(...xs) - 0.5);
    for (let x = xa; x <= xb; x++) { const c = col(x, y); if (c) p.set(x, y, c); }
  }
}
/** a box on the bridge: s0..s1 along it, o0..o1 across (o > 0 towards us), h0..h1 high; paints its near face (o1) and
    its east face (s1), the two we see from the south-east, and its top when it's under the eye */
function brBox(p, s0, s1, o0, o1, h0, h1, ramp, lit = 0, topCol = null) {
  const P = (s, o, h) => brAt(s, o, h);
  fillPoly(p, [P(s1, o1, h0), P(s1, o0, h0), P(s1, o0, h1), P(s1, o1, h1)], (x, y) => pick(ramp, 0.22 + lit * 0.6 + (vnoise(x / 2, y / 2, 11) - 0.5) * 0.12, x, y, 0.7));
  fillPoly(p, [P(s0, o1, h0), P(s1, o1, h0), P(s1, o1, h1), P(s0, o1, h1)], (x, y) => pick(ramp, 0.4 + lit + (vnoise(x / 2, y / 2, 12) - 0.5) * 0.14, x, y, 0.7));
  if (topCol && h1 < EYE) fillPoly(p, [P(s0, o1, h1), P(s1, o1, h1), P(s1, o0, h1), P(s0, o0, h1)], () => topCol);
}
const BR_LAMPS = [];                                // the eight globes: { x, y, far } (lit per frame)
const BR_WL = new Float32Array(W).fill(0);           // the bridge's near waterline per column (its mirror's axis)
function renderBridge() {
  const back = pix(W, H), front = pix(W, H), L = BR_LEN;
  BR_LAMPS.length = 0;
  for (let s = 0; s <= L; s += 0.05) { const [x, y] = brAt(s, BR.half + 0.4, 0); const xi = Math.round(x); if (xi >= 0 && xi < W) BR_WL[xi] = y; }
  // the deck's top (a sliver: the eye is just above it) and the far railing, behind the couple
  brBox(back, 0, L, -BR.half, BR.half, BR.deckLo, BR.deck, GRAN, 0.1, '#3a3836');
  const railLine = (p, o, dim) => {
    for (let s = 0; s <= L; s += 0.04) {
      const [x, yb] = brAt(s, o, BR.deck), [, yt] = brAt(s, o, BR.rail), xi = Math.round(x);
      const ya = Math.round(yt), yz = Math.round(yb) - 1;
      for (let y = ya; y <= yz; y++) {
        const k = y - ya, lat = (xi + y + 600) % 6 === 0 || (xi - y + 600) % 6 === 0;
        if (k === 0) p.set(xi, y, dim ? IRON.dark : IRON.lit);
        else if (lat || y === yz) p.set(xi, y, dim ? mixHex(IRON.dark, '#101420', 0.3) : IRON.mid);
      }
    }
  };
  railLine(back, -BR.half, true);
  // the chains: tower top to tower top over the main span, down to the end posts over the side spans
  const chain = (p, o, col) => {
    const hT = BR.towerTop - 0.25, [a, b] = PIER_C;
    const hAt = s => s < a ? lerp(BR.post, hT, (s - 0.5) / (a - 0.5)) - 0.35 * Math.sin(Math.PI * (s - 0.5) / (a - 0.5))
      : s > b ? lerp(hT, BR.post, (s - b) / (L - 0.5 - b)) - 0.35 * Math.sin(Math.PI * (s - b) / (L - 0.5 - b))
        : BR.rail + 0.15 + (hT - BR.rail - 0.15) * Math.pow(Math.abs(s - (a + b) / 2) / ((b - a) / 2), 2);
    for (let s = 0.5; s <= L - 0.5; s += 0.04) { const [x, y] = brAt(s, o, hAt(s)); p.set(x, y, col); }
  };
  // a tower: the shaft on the pier, the cornice, the lamp post; o: its axis across the bridge
  const tower = (p, sc, o, far) => {
    brBox(p, sc - 0.6, sc + 0.6, o - 0.6, o + 0.6, BR.deck - 0.3, BR.towerTop - 0.35, GRAN, far ? 0.08 : 0.3);
    brBox(p, sc - 0.6, sc + 0.6, o - 0.6, o + 0.6, BR.towerTop - 1.0, BR.towerTop - 0.35, GRAN, far ? 0.2 : 0.5);   // its head, lit by the lamp above
    brBox(p, sc - 0.75, sc + 0.75, o - 0.75, o + 0.75, BR.towerTop - 0.35, BR.towerTop, GRAN, far ? 0.3 : 0.6, '#c0b4a2');
    for (let h = BR.towerTop; h < BR.lampTop; h += 0.04) { const [x, y] = brAt(sc, o, h); p.set(x, y, far ? IRON.dark : IRON.post); }
    const [gx, gy] = brAt(sc, o, BR.lampTop + 0.25);
    BR_LAMPS.push({ x: Math.round(gx), y: Math.round(gy), far });
  };
  const endPost = (p, sc, o, far) => {
    brBox(p, sc - 0.5, sc + 0.5, o - 0.5, o + 0.5, BR.deck - 0.2, BR.post, GRAN, far ? 0.1 : 0.3, '#8a827c');
    for (let h = BR.post; h < BR.postLamp; h += 0.04) { const [x, y] = brAt(sc, o, h); p.set(x, y, far ? IRON.dark : IRON.post); }
    const [gx, gy] = brAt(sc, o, BR.postLamp + 0.2);
    BR_LAMPS.push({ x: Math.round(gx), y: Math.round(gy), far });
  };
  chain(back, -BR.tower, IRON.dark);
  for (const sc of PIER_C) tower(back, sc, -BR.tower, true);
  endPost(back, 0.5, -BR.tower, true); endPost(back, L - 0.5, -BR.tower, true);
  // in front: the piers in the water, the abutments, the girder's fascia, the near railing, posts, towers and chain
  for (const [a, b] of PIERS) {
    brBox(front, a, b, -2.9, 2.9, -0.3, BR.deck - 0.35, GRAN, 0.05, null);
    brBox(front, a - 0.15, b + 0.15, -3.05, 3.05, BR.deck - 0.55, BR.deck - 0.3, GRAN, 0.35, null);   // the corbelled cap
  }
  brBox(front, -1.2, 0.2, -3, 3, -0.3, BR.deck, GRAN, 0.1, null);              // the west abutment
  brBox(front, L - 0.2, L + 1.6, -3, 3, -0.3, BR.deck, GRAN, 0.15, null);      // the east abutment, steps down to the landing
  for (let k = 0; k < 4; k++) brBox(front, L + 1.6 + k * 0.45, L + 2.05 + k * 0.45, 1.2, 3.2, -0.3, BR.deck - 0.55 - k * 0.6, GRAN, 0.25 - k * 0.04, '#5a5452');
  fillPoly(front, [brAt(0, BR.half, BR.deckLo), brAt(L, BR.half, BR.deckLo), brAt(L, BR.half, BR.deck), brAt(0, BR.half, BR.deck)], (x, y) => pick(GRAN, 0.36, x, y));
  for (let s = 0; s <= L; s += 0.04) { const [x, y] = brAt(s, BR.half, BR.deck); front.set(x, y, "#8a847e"); }
  railLine(front, BR.half, false);
  endPost(front, 0.5, BR.tower, false); endPost(front, L - 0.5, BR.tower, false);
  for (const sc of PIER_C) tower(front, sc, BR.tower, false);
  chain(front, BR.tower, IRON.lit);
  return { back: trim(back), front: trim(front) };
}

/* ================= The Swan Boat landing ================= */
// The pavilion: a low green hip roof on green posts over the float, the ticket counter shuttered for the night, a lantern
// under the eave, the flag on its pole at the float's corner. The float lies level on the water: a planked deck, its lit
// front edge, the dark green pontoon side and a black waterline, doubled underneath (PAV_REF), with a railed gangway
// down to the bank's path. The boats: green twin pontoons trimmed red, six rows of slatted benches with orange-red
// frames, the white swan at the stern hiding the pedalling seat. Tied up sterns north.
const GREEN = ['#0a1a14', '#12281c', '#1c3a26', '#285030', '#36663c'];
const PAV_WL = PAV.float + 2;                       // the float's waterline row: its reflection starts under it
function renderPavilion() {
  const p = pix(W, H), { x0, x1, roof, eave, float: fy } = PAV;
  for (let x = x0 - 3; x <= x1 + 4; x++) {                                      // the float: deck, lit edge, pontoon, waterline
    const lit = Math.exp(-(((x - x0 - 16) / 16) ** 2));                          // the lantern under the eave
    p.set(x, fy - 2, x % 4 === 0 ? '#4a4236' : '#5e5444');
    p.set(x, fy - 1, mixHex('#5a4c3a', '#9a8464', lit));
    p.set(x, fy, x % 5 === 0 ? '#12241a' : '#1e3a2a'); p.set(x, fy + 1, '#0e1c16');
    p.set(x, PAV_WL, '#03050a');
  }
  for (let x = x0 - 18; x < x0 - 3; x++) { p.set(x, fy, '#5a5040'); p.set(x, fy + 1, '#1a2e22'); p.set(x, PAV_WL, '#03050a'); }   // the finger float the boats tie to
  const posts = [x0 + 1, x0 + 9, x0 + 17, x0 + 24, x1 - 1];
  for (let y = eave + 1; y < fy - 2; y++) for (let x = x0; x <= x1; x++) {     // inside: the shuttered counter, benches in the dark
    if (posts.includes(x)) { p.set(x, y, '#3a7048'); p.set(x + 1, y, '#1a3424'); continue; }
    const lit = Math.exp(-(((x - x0 - 16) / 12) ** 2) - (((y - eave - 4) / 9) ** 2));
    if (y > fy - 10 && x > x0 + 9 && x < x0 + 24) p.set(x, y, y === fy - 10 ? '#8a7050' : (x + y) % 4 === 0 ? '#3a2c22' : mixHex('#4a3a2a', '#8a6a44', lit));   // the counter's shutter
    else if (y < eave + 4) p.set(x, y, mixHex('#2a2a22', '#7a6448', lit));      // the ceiling boards
    else p.set(x, y, pick(['#0c100e', '#1a1a14', '#2e2a20', '#4a3e2c'], 0.15 + lit * 0.8, x, y));
  }
  for (let x = x0 - 2; x <= x1 + 3; x++) {                                       // the roof: eave line, slopes, ridge
    const u = (x - x0 + 2) / (x1 - x0 + 5), hip = Math.round(Math.min(u, 1 - u) * 2 * (eave - roof));
    for (let y = eave - Math.min(hip, eave - roof); y <= eave; y++) p.set(x, y, pick(GREEN, (y === eave ? 0.85 : 0.35) + (u < 0.3 ? 0.15 : 0) - (y - roof) * 0.04, x, y));
    p.set(x, eave + 1, '#16301e');
  }
  for (let x = x0 + 6; x <= x1 - 6; x++) p.set(x, roof, '#4a7a50');             // the ridge, catching the bridge's lamps
  for (let y = fy - 25; y < fy - 1; y++) p.set(x1 + 3, y, '#9aa0a0');             // the flagpole
  p.set(x1 + 3, fy - 26, '#e8d070');
  // the float and pavilion doubled under the waterline: the pontoon's side black-green, then the lit deck edge, dimming
  const ref = pix(W, 8), wat = [6, 10, 24];
  for (let k = 0; k < 8; k++) for (let x = x0 - 18; x <= x1 + 4; x++) {
    const sy = PAV_WL - 1 - k, o = (sy * W + x) * 4;
    if (!p.d[o + 3] || (k > 2 && bayer(x, k) < (k - 2) * 0.12)) continue;
    const f = 0.4 + k * 0.12;
    ref.set(x, k, [0, 1, 2].map(c => Math.round((p.d[o + c] * (1 - f) + wat[c] * f) * 0.85)), Math.round(255 * (0.95 - k * 0.06)));
  }
  PAV_REF = ref.done();
  // the gangway from the end of the bank's path up to the float's deck, over the open water: planks, a shadowed side, a
  // green rail either side on three posts
  const g0 = fy - 1, g1 = PAV_WL + 5, edge = (y, s) => { const u = (y - g0) / (g1 - 1 - g0); return Math.round(lerp(315, 313, u) + s * lerp(1.2, 2.2, u)); };
  for (let y = g0; y < g1; y++) {
    for (let x = edge(y, -1); x <= edge(y, 1); x++) p.set(x, y, y % 2 ? '#5a4e3e' : '#706250');
    p.set(edge(y, -1) - 1, y, '#0a0e12');
  }
  for (const s of [-1, 1]) {
    const rh = y => y > g0 + 3 ? 3 : 2;
    p.line(edge(g1 - 1, s), g1 - 1 - rh(g1 - 1), edge(g0, s), g0 - rh(g0), s < 0 ? '#2e5a3c' : '#3e6e4a');
    for (const y of [g0, Math.round((g0 + g1 - 1) / 2), g1 - 1]) for (let k = 1; k < rh(y); k++) p.set(edge(y, s), y - k, '#1a3424');
  }
  return trim(p);
}
// a swan boat, broadside, bow to the left (south), the swan at the stern on the right, facing forward
const BOAT_ROWS = [
  '.........................................ww...........',
  '........................................bwww..........',
  '..........................................ww..........',
  '...........................................ww.........',
  '...........................................ww.wwwww...',
  'l.........ooo..ooo..ooo..ooo..ooo..ooo.....wwwwwwwwwww',
  '.l........yyy..yyy..yyy..yyy..yyy..yyy....wwwwwwwwwwww',
  '..l.r.....ooo..ooo..ooo..ooo..ooo..ooo....WWWWWWWWWWW.',
  '...rrrrrrrdddddddddddddddddddddddddddddddrrrrrrrrrrrrr',
  '....gggggggggggggggggggggggggggggggggggggggggggggggggg',
  '.....GGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGG.'];
const BOAT_KEY = { w: '#f4f2ea', W: '#b8b6b0', b: '#d88a3a', o: '#b8502e', y: '#8a6440', d: '#5a4a3a', r: '#a8322a', g: '#1e6a4a', G: '#0e3a28', l: '#6a6460' };
const BOAT_KEY_FAR = Object.fromEntries(Object.entries(BOAT_KEY).map(([k, c]) => [k, mixHex(c, '#0a0e1e', 0.35)]));
const BOAT_SCR = [];                                // each boat's screen anchor (left end of the waterline) and size
function placeBoats() {
  BOAT_SCR.length = 0;
  for (const b of BOATS) {
    const [x, y, d] = pr(b.lx, b.ly, 0), sc = (S * DEG / d) * 9.1 * 0.94 / BOAT_ROWS[0].length;   // the sprite's px per sprite px
    BOAT_SCR.push({ x: Math.round(x - BOAT_ROWS[0].length * sc / 2), y: Math.round(y), d, sc, outer: b.outer });
  }
}

/* ================= The water ================= */
// Which pixels are lagoon: the south lobe up to the near curb; the bank strip to the landing; the waist from the boats
// to the bridge and on under it into the north lobe; the north lobe's strip behind the east bank.
const WMASK = new Uint8Array(W * H);
function buildMask() {
  for (let x = 0; x < W; x++) for (let y = 0; y < H; y++) {
    let w = false;
    if (y >= FAR[x] && y <= CURB) {
      if (x < 246) w = true;
      else if (x < 300) w = y <= keyAt(BANK_FAR, Math.min(x, 284)) || y >= keyAt(BANK_NEAR, x);
      else if (x < 392) w = y <= keyAt(SHORE_E, x);
      else w = y <= 158;
    }
    WMASK[y * W + x] = w ? 1 : 0;
  }
}
const isWater = (x, y) => x >= 0 && x < W && y >= 0 && y < H && WMASK[Math.round(y) * W + Math.round(x)] === 1;
function renderWater() {
  const p = pix(W, H), P = ['#05081a', '#070b20', '#0a0f27', '#0e142e', '#121a36'];
  for (let y = 150; y <= CURB; y++) for (let x = 0; x < W; x++) if (WMASK[y * W + x])
    p.set(x, y, pick(P, 0.75 - 0.5 * (y - 150) / (CURB - 150) + (vnoise(x / 30, y / 3, 9) - 0.5) * 0.2, x, y, 0.5));
  return p.done();
}
/** the mirror: what stands over the water, flipped about each column's waterline, softened and dimmed */
function renderMirror(src) {
  const sx = src.getContext('2d'), data = sx.getImageData(0, 0, W, H).data, p = pix(W, H);
  for (let x = 0; x < W; x++) for (let y = 150; y <= CURB; y++) {
    if (!WMASK[y * W + x]) continue;
    const under = x >= 244 && x < 392, axis = under && y >= BR_WL[x] && BR_WL[x] ? BR_WL[x] : FAR[x];
    const sy0 = axis - (y - axis) * 1.08, acc = [0, 0, 0];
    let n = 0;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const yy = Math.round(sy0 + dy), xx = clamp(x + dx, 0, W - 1);
      if (yy < 0 || yy >= axis) continue;
      const o = (yy * W + xx) * 4;
      acc[0] += data[o]; acc[1] += data[o + 1]; acc[2] += data[o + 2]; n++;
    }
    if (!n) continue;
    const f = 0.72 - (y - axis) * 0.006;
    p.set(x, y, [0, 1, 2].map(c => Math.min(255, Math.floor((acc[c] / n * f + [6, 9, 30][c] * (1 - f)) / 8 + bayer(x, y)) * 8)));
  }
  return p.done();
}
const GLINTS = [];                                  // ripples catching the light, drifting
const RING_SPOTS = [];                              // where drips and drops may land on the water: [x, y]
{
  const r = rng(733);
  for (let i = 0; i < 110; i++) GLINTS.push({ y: 158 + Math.floor(r() * (CURB - 160)), x: r(), k: (r() < 0.5 ? -1 : 1) * (1 + Math.floor(r() * 3)),
    len: 2 + Math.floor(r() * 5), kf: 18 + Math.floor(r() * 40), ph: r(), c: r() < 0.3 ? '#a88a64' : r() < 0.75 ? '#3e4878' : '#03050f' });
}

/* ================= The ground: the near path, the east bank's lawns, all still wet ================= */
const PAVE = ['#0e1018', '#141722', '#1b1f2c', '#252a38', '#313646'];
const LAWNC = ['#060c08', '#0a130c', '#0f1a10', '#152316', '#1c2e1a', '#26381e'];
const WARMG = ['#2a2620', '#463c30', '#6a583e', '#8e7450', '#b49466', '#d8b880'];
const GRANC = ['#262832', '#363844', '#4a4c58', '#60626c'];
/** warm light on the ground from the lamp by the bench, the cart, the bridge's lamps over the east bank */
function groundLight(x, y) {
  let s = 0.62 * Math.exp(-(((x - LAMP_N.x) / 34) ** 2) - (((y - 206) / 10) ** 2));
  s += 0.5 * Math.exp(-(((x - (CART.x0 + CART.x1) / 2) / 30) ** 2) - (((y - 201) / 9) ** 2));
  s += 0.22 * Math.exp(-(((x - 392) / 26) ** 2) - (((y - 166) / 8) ** 2)) + 0.16 * Math.exp(-(((x - 330) / 24) ** 2) - (((y - 176) / 6) ** 2));
  return Math.min(1, s);
}
const LAWN_PX = new Uint8Array(W * H);              // which pixels are plain lawn (the grass waves roll over them)
const TULIPS = [];                                  // the bed's nearer tulips, drawn per frame so the breeze can move them
const PUDDLES = [[150, 205, 9], [232, 208, 6], [338, 204, 8], [402, 206, 5], [74, 209, 5]];   // [x, y, half width]
const inPuddle = (x, y) => PUDDLES.some(([px, py, hw]) => Math.abs(y - py) <= 1 && Math.abs(x - px) <= hw - (y === py ? 0 : 2));
function renderGround() {
  const p = pix(W, H);
  TULIPS.length = 0; LAWN_PX.fill(0);
  const landAt = (x, y) => y > CURB || (y >= 152 && !WMASK[y * W + x] && (x >= 300 ? y > keyAt(SHORE_E, x) || (x >= 392 && y > 158)
    : x >= 246 && y > keyAt(BANK_FAR, Math.min(x, 284)) && y < keyAt(BANK_NEAR, x)));
  for (let x = 0; x < W; x++) for (let y = 152; y < H; y++) {
    if (!landAt(x, y)) continue;
    const g = groundLight(x, y), near = y > CURB;
    let col;
    if (near && y <= CURB + 2) col = x % 19 === 0 ? '#14151c' : g > 0.2 ? pick(WARMG, 0.25 + g * 0.5 - (y - CURB) * 0.1, x, y) : pick(GRANC, 0.7 - (y - CURB - 1) * 0.3, x, y);
    else if (near && y === CURB + 3) col = '#0a0b12';
    else if (near && y < LAWN + Math.round(1.5 * vnoise(x / 6, 1, 27) - 0.7)) {
      const v = 0.38 + (vnoise(x / 12, y / 3, 23) - 0.5) * 0.16 + (hash(x, y, 24) - 0.5) * 0.08;
      const sheen = g * 0.55 * (hash(x >> 1, y, 28) < 0.4 ? 1 : 0.4);
      col = inPuddle(x, y) ? pick(['#1a2040', '#262c50', '#3a3c5c'], 0.4 + g, x, y) : sheen + (bayer(x, y) - 0.5) * 0.25 > 0.42 ? pick(WARMG, 0.1 + sheen * 0.55, x, y, 0.6) : pick(PAVE, v + g * 0.35, x, y, 0.6);
    } else {
      const far = !near, v = 0.34 + (vnoise(x / 6, y / 2, 25) - 0.5) * 0.3 + (near ? (y - LAWN) * 0.004 : (y - 160) * 0.006) - (far && y < 165 ? 0.12 : 0);
      col = pick(LAWNC, v + g * 0.55, x, y, 0.8);
      LAWN_PX[y * W + x] = 1;
      if (g > 0.3 && hash(x, y, 26) < g * 0.25) col = '#3a5228';            // wet blades catching the light
    }
    p.set(x, y, col);
  }
  // the east bank's paths: down to the landing's gangway, and up to the bridge's steps
  for (let k = 0; k <= 1; k += 0.01) {
    const [ax, ay, bx2, by, w0, w1] = k >= 0 ? [306, 197, 313, 178, 7, 2.5] : [];
    const x = lerp(ax, bx2, k), y = lerp(ay, by, k), hw = lerp(w0, w1, k);
    for (let i = -hw; i <= hw; i++) p.set(x + i, y, pick(PAVE, 0.45 + groundLight(x, y) * 0.5, Math.round(x + i), Math.round(y)));
    const x2 = lerp(368, 391, k), y2 = lerp(197, 163, k), hw2 = lerp(8, 2, k);
    for (let i = -hw2; i <= hw2; i++) p.set(x2 + i, y2, pick(PAVE, 0.45 + groundLight(x2, y2) * 0.6, Math.round(x2 + i), Math.round(y2)));
  }
  // the beds of the last tulips, on the bank by the landing and on the near lawn: upright stems, wet heads, lamp-lit
  const bed = (cx, cy, rx, ry, dens, seed) => {
    for (let y = cy - ry; y <= cy + ry; y++) for (let x = cx - rx; x <= cx + rx; x++) {
      const d = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
      if (d > 1) continue;
      LAWN_PX[y * W + x] = 0;
      p.set(x, y, pick(LAWNC, 0.1 + (1 - d) * 0.1, x, y));                    // the bed's dark soil
      if ((y - cy + ry) % 2 || hash(x, y, seed) > dens) continue;
      const g = groundLight(x, y), rim = d > 0.62, c = rim ? (hash(x, y, seed + 1) < 0.7 ? "#e8c03a" : "#f0e8d0") : (hash(x, y, seed + 1) < 0.75 ? "#c8303a" : "#d86a8a");
      const head = mixHex(c, "#0a0c18", 0.62 - g * 0.5);
      if (seed === 65 && y > cy - 6 && hash(x, y, 66) < 0.45) { TULIPS.push({ x, y, head, tall: hash(x, y, 67) < 0.4 ? 1 : 0, ph: hash(x, y, 68) }); continue; }   // the nearer ones sway (per frame)
      p.set(x, y - 1, head); p.set(x, y, "#1e3a1c");
    }
  };
  bed(335, 181, 13, 3, 0.55, 63);
  bed(250, 247, 150, 16, 0.5, 65);
  for (let x = 96; x < 404; x++) { const yb = 247 - Math.round(16 * Math.sqrt(Math.max(0, 1 - ((x - 250) / 150) ** 2))); if (yb < 247) { p.set(x, yb - 1, '#2e4428'); p.set(x, yb - 2, '#1a2a18'); } }   // its clipped box edging
  // the low iron hoop fence along the lawn's edge, as all over the garden
  for (let x = 0; x < W; x++) {
    const ph = x % 7, y = LAWN + 1 + 3 - Math.round(3 * Math.sin(Math.PI * ph / 7)), g = groundLight(x, y);
    p.set(x, y, g > 0.25 ? "#5a5248" : "#16181c"); LAWN_PX[y * W + x] = 0;
    if (ph === 0) { p.set(x, y + 1, "#121418"); p.set(x, y + 2, "#121418"); }
  }
  return trim(p);
}
/* the garden lamp by the bench: a dark green post, a white globe on a fluted collar; its foot stands on the path's
   edge just past the curb (row LAMP_FOOT), its shadow under it */
const LAMP_FOOT = CURB + 4;
function renderLamp() {
  const h = LAMP_FOOT - LAMP_N.y + 5, p = pix(9, h), K = '#0c1410', KH = '#24382c', cx = 4;
  for (let y = 8; y < h - 3; y++) { p.set(cx, y, K); p.set(cx - 1, y, y < 30 ? KH : '#16241c'); }
  for (let y = h - 9; y < h - 1; y++) { const hw = y < h - 6 ? 1 : 2; for (let x = cx - hw; x <= cx + hw; x++) p.set(x, y, x === cx - hw ? KH : K); }
  for (let x = 1; x < 8; x++) p.set(x, h - 1, '#04060e', x > 1 && x < 7 ? 120 : 60);
  for (let x = cx - 2; x <= cx + 2; x++) { p.set(x, 6, KH); p.set(x, 7, K); }
  for (let y = 0; y < 6; y++) for (let x = cx - 2; x <= cx + 2; x++) {
    if ((y === 0 || y === 5) && Math.abs(x - cx) === 2) continue;
    p.set(x, y, Math.abs(x - cx) === 2 ? '#f0e0b8' : y < 3 ? '#fffcf0' : '#fff2d0');
  }
  return p.done();
}
/* a park bench beside the path, seen from behind at an angle: slatted back and seat, iron ends */
function renderBench() {
  const p = pix(30, 13), K = '#0e1014', WOOD = '#3a2a1e', WOODL = '#5a422e', WOODH = '#7a5a3c';
  for (let x = 1; x < 29; x++) { p.set(x, 0, WOODH); p.set(x, 1, WOOD); p.set(x, 3, WOODL); p.set(x, 4, WOOD); p.set(x, 7, WOODL); p.set(x, 8, '#2a1e16'); }
  for (const lx of [2, 27]) for (let y = 0; y < 13; y++) { p.set(lx, y, K); if (y > 8) p.set(lx + (lx < 10 ? -1 : 1), y, '#1e2028'); }
  return p.done();
}
/* the near willow at the right edge: a leaning, furrowed trunk, its long hanging strands drawn per frame (they sway) */
const BARK = ['#14120e', '#221e18', '#342e24', '#4a4232', '#625842'];
const STRANDS = [];                                 // { x, y0, len, ph, col } hanging from the crown
function renderWillowTrunk() {
  const p = pix(W, H), { x: tx, base } = WILLOW_R;
  for (let y = 40; y <= base; y++) {
    const u = (base - y) / (base - 40), w = Math.round(lerp(16, 8, u)), x0 = Math.round(tx - w / 2 + 10 * u * u - 4 * u);
    for (let x = x0; x < x0 + w; x++) {
      const fur = vnoise(x / 1.6, y / 6, 701);
      let v = fur > 0.6 ? 0.7 : fur > 0.4 ? 0.45 : 0.2;
      if (x === x0) v += 0.15; if (x >= x0 + w - 2) v -= 0.2;
      p.set(x, y, pick(BARK, v * 0.7, x, y, 0.6));
    }
  }
  for (const [x0, y0, x1, y1] of [[466, 90, 420, 52], [470, 70, 446, 26], [472, 60, 486, 18], [468, 110, 430, 80]]) {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let k = 0; k <= n; k++) { const u = k / n, w = Math.max(1, Math.round(4 * (1 - u))); for (let i = 0; i < w; i++) p.set(lerp(x0, x1, u) + i, lerp(y0, y1, u), pick(BARK, 0.35, Math.round(lerp(x0, x1, u)) + i, Math.round(lerp(y0, y1, u)))); }
  }
  // the crown's mass behind the strands
  for (let y = 0; y < 96; y++) for (let x = 404; x < W; x++) {
    const d = Math.hypot((x - 462) / 62, (y - 30) / 52), n = vnoise(x / 5, y / 4, 703);
    if (d + (n - 0.5) * 0.5 > 1 || (x < 452 && y > 64)) continue;
    p.set(x, y, pick(WILLOWC, 0.18 + (n - 0.5) * 0.4 + (y > 40 ? 0.05 : 0), x, y, 0.8));
  }
  const r = rng(709);
  for (let i = 0; i < 96; i++) {                     // the hanging strands: long near the trunk, kept clear of the sign
    const x = 404 + Math.floor(r() * 76), y0 = 16 + Math.floor(r() * 56);
    const maxLen = x < 452 ? Math.max(4, 114 - y0) : 172 - y0, len = Math.round(maxLen * (0.45 + r() * 0.55)), runs = [];
    for (let k = 0; k < len;) { const n = 1 + Math.floor(r() * 4); runs.push([k, Math.min(n, len - k)]); k += n + (r() < 0.35 ? 1 : 0); }
    const v = 0.22 + r() * 0.4, lit = x > 410 && x < 452 ? 0.25 : 0;
    STRANDS.push({ x, y0, len, runs, ph: r(), curl: r() * 6, col: pick(WILLOWC, v, x, y0), tip: pick(WILLOWC, v + 0.15 + lit, x, y0 + len) });
  }
  return trim(p);
}
/* the hawthorn in blossom by the landing, whose petals the breeze takes */
function renderHawthorn() {
  const p = pix(W, H), { x: cx, y: cy } = HAWTHORN;
  for (let y = cy; y < cy + 9; y++) { p.set(cx, y, '#1a140e'); p.set(cx + 1, y, '#2a2018'); }
  for (let y = cy - 14; y < cy + 2; y++) for (let x = cx - 11; x <= cx + 12; x++) {
    const d = Math.hypot((x - cx - 0.5) / 12, (y - cy + 6) / 8), n = vnoise(x / 2.5, y / 2.5, 721);
    if (d + (n - 0.5) * 0.5 > 1) continue;
    const bloom = hash(x, y, 722) < 0.12 + (y < cy - 7 ? 0.1 : 0) && n > 0.42;
    p.set(x, y, bloom ? mixHex('#ecd8e0', '#0c1020', 0.45 + (x - cx) * 0.012) : pick(LAWNC, 0.32 + (n - 0.5) * 0.45, x, y));
  }
  return trim(p);
}
/* ================= Dora's cream pie cart, and its sign (the city's billboard) ================= */
// A wooden cart painted cream, its chocolate-brown trim and a striped canopy, a lamp under the canopy over the glass case
// of pies; on the canopy two posts carry the lit sign. The sign, its posts and frame are the billboard; the cart stays.
function renderCart() {
  const p = pix(W, H), { x0, x1, top, counter, base } = CART;
  for (let x = x0 - 2; x <= x1 + 2; x++) for (let y = top; y < top + 5; y++) {   // the canopy, chocolate and cream stripes
    const stripe = ((x - x0 + 40) >> 2) & 1;
    p.set(x, y, y === top ? '#e8dcc0' : stripe ? '#e8dcc0' : '#5a3424');
    if (y === top + 4 && x % 4 === 0) p.set(x, y + 1, stripe ? '#e8dcc0' : '#5a3424');   // the scalloped valance
  }
  for (const px2 of [x0, x1]) for (let y = top + 5; y < counter; y++) p.set(px2, y, '#3a2418');
  for (let x = x0 + 1; x < x1; x++) for (let y = counter - 6; y < counter; y++)      // the glass case of pies, lit
    p.set(x, y, y === counter - 6 ? '#d8d0c0' : y > counter - 3 ? (x % 5 < 3 ? '#e8c068' : '#5a3020') : '#f8e8c0');
  for (let x = x0 - 1; x <= x1 + 1; x++) for (let y = counter; y < base - 3; y++) {   // the cart's body
    let col = y === counter ? '#5a3424' : y === counter + 1 ? '#d8ccb0' : (x === x0 - 1 || x === x1 + 1) ? '#3a2418' : mixHex('#c8b894', '#2a2430', (y - counter) / 40);
    if (y > counter + 3 && y < base - 6 && (x - x0) % 11 === 5) col = '#a8987a';
    p.set(x, y, col);
  }
  for (let x = x0 - 1; x <= x1 + 1; x++) for (let y = base - 3; y < base; y++) p.set(x, y, '#04060e', y === base - 3 ? 130 : 80);   // its shadow on the ground
  for (const wx of [x0 + 5, x1 - 5]) for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {   // spoked wheels
    const d = Math.hypot(dx, dy);
    if (d > 3.3) continue;
    p.set(wx + dx, base - 3 + dy, d > 2.4 ? '#1a1410' : (dx === 0 || dy === 0) ? '#5a4a3a' : null || '#2a2018');
  }
  return trim(p);
}
function renderBoard() {
  const p = pix(W, H), L = BB.x - 2, Rr = BB.x + BB.w + 1, T0 = BB.y - 2, B = BB.y + BB.h + 1;
  for (const lx of [BB.x + 7, BB.x + BB.w - 8]) for (let y = B; y < CART.top; y++) { p.set(lx, y, '#2a1a12'); p.set(lx + 1, y, '#1a100c'); }
  for (let y = T0; y <= B; y++) for (let x = L; x <= Rr; x++) {
    let col = '#3a2418';
    if (y === T0 || x === L) col = '#6a4a34'; else if (y === B || x === Rr) col = '#1e120c';
    p.set(x, y, col);
  }
  for (const lx of [BB.x + 9, BB.x + BB.w - 10]) { p.line(lx, T0, lx, T0 - 3, '#2a2420'); p.line(lx, T0 - 3, lx + 3, T0 - 4, '#2a2420'); p.set(lx + 3, T0 - 3, '#3a3430'); }
  return trim(p);
}
// a slice of Boston cream pie: golden sponge, custard, the chocolate glaze on top
const SLICE = ['ccccccccc.', '.ccccccc..', '.sssssss..', '..kkkkk...', '..sssss...', '...sss....', '....s.....'];
// N, M and W drawn wide on the signs: at 1x FONT3's N reads as a Π and its M and W as an H
const WIDE = { N: ['1001', '1101', '1011', '1001', '1001'], M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'] };
const signGlyph = ch => WIDE[ch] ?? FONT3[ch];
const signW = s => [...s].reduce((w, ch) => w + (signGlyph(ch)?.[0].length ?? 3) + 1, 0) - 1;
function sign(c, s, x, y, col) { for (const ch of s) { const g = signGlyph(ch); if (g) glyph(c, g, x, y, col); x += (g?.[0].length ?? 3) + 1; } }
function renderAd() {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, pick(['#d8c8a0', '#ecdcb4', '#f8ecc8'], 0.85 - j * 0.02, i, j));
  for (let i = 0; i < w; i++) { p.set(i, 0, '#5a3424'); p.set(i, h - 1, '#5a3424'); }
  const key = { c: '#4a2418', s: '#d8a040', k: '#f4e090' };
  SLICE.slice(0, 6).forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) p.set(2 + i, 8 + j, key[row[i]]); });
  const c = p.done(), cx = c.getContext('2d');
  const ink = (str, x, y, col) => sign(cx, str, x, y, col);
  ink("DORA'S", Math.round((w - signW("DORA'S")) / 2), 2, '#8a2a1e');
  ink('BOSTON', 14, 8, '#4a2418');
  ink('CREAM PIE', 5, 14, '#4a2418');
  return gradeAd(c, { level: 0.74, tint: '#141a3a', mix: 0.16, wear: 0.15, seed: 21 });
}
/* the vignette: the frame's corners a touch darker */
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.48) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [4, 6, 18], Math.round(q * 0.42 * 255));
  }
  return p.done();
}

/* ================= Weather ================= */
// The city's own: the rain that has just passed, back for one last shower (60-118) and dripping from the willows all
// night; the breeze's hawthorn petals. A picked rain rains all loop; any other pick replaces both (drawWeather).
const WEATHER = weatherPlans(['rain', 'petals']);
const WX_LAND = [[158, CURB], [CURB + 2, LAWN + 6], [H + 4, H + 4]];
const WX_LIGHTS = [{ x: LAMP_N.x, y: LAMP_N.y + 24, rx: 30, ry: 40 }, { x: 330, y: 132, rx: 60, ry: 22 }, { x: (CART.x0 + CART.x1) / 2, y: 178, rx: 26, ry: 22 }];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + 23, y: BB.y + 8, rx: 30, ry: 16, col: '#ffe8c0' }] };

/* ================= People ================= */
// One function draws everyone (after rome.js and boston.js): a look gives the proportions for a height and the colours
// dimmed for the night; person() puts it on its feet. Late-May clothes just after rain: light jackets, a raincoat, a
// hoodie, jeans. The shoulders and hair catch a line of the lamps' warm light.
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#c8c4c0'];
const NIGHT = '#0a0e28', GLOWC = '#f0d0a0';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.3), bot: m(bot), shoe: m('#1a161c'), long: o.long || 0, coat: o.coat || 0, shorts: o.shorts || 0,
    hat: o.hat ? m(o.hat) : null, dress: o.dress || 0 };
}
/**
 * One person, feet on row fy, centred on column x; returns the top row of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up to the face (a book, a phone), 3 out, 4 up and out (pointing), 5 round a
 * neighbour's back; o.lean: the head towards a neighbour; o.sit: seated; o.bend: bent forward (in profile)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : o.crouch ? Math.ceil(P.ll / 2) : P.ll, bend = side && o.bend ? 1 : 0;
  const ty = fy - legs - th + 1 + bend, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1) + bend * dir;
  if (legs) {
    const ly = fy - legs + 1, low = P.shorts ? Math.ceil(legs * 0.55) : 0, legC = P.dress ? P.top : P.bot;
    ctx.fillStyle = 'rgba(6,8,22,0.36)'; ctx.fillRect(x - (bw >> 1) - 2, fy + 1, bw + 4, 1); ctx.fillRect(x - (bw >> 1), fy + 2, bw, 1);
    const leg = (lx, ly0, n) => { R(lx, ly0, 1 + (side ? 1 : 0), n, legC); if (low) R(lx, fy - low + 1, 1 + (side ? 1 : 0), low - 1, P.skin); };
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        leg(dir > 0 ? x + 1 : x - 2, ly + 2, legs - 2); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        leg(dir > 0 ? x - 2 : x + 1, ly + 2, legs - 3); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { leg(x - 1, ly, legs); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, legC); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      if (low) { R(tx, fy - low + 1, lw, low - 1, P.skin); R(tx + bw - lw, fy - low + 1, lw, low - 1, P.skin); }
      if (P.dress) R(tx - 1, ly, bw + 2, 3, P.top);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) { R(tx - 1, fy - 1, bw + 2, 2, P.bot); if (o.dangle) { R(tx, fy + 1, 2, 4, P.bot); R(tx + bw - 2, fy + 1, 2, 4, P.bot); } }
  R(tx, ty, bw, th + P.coat, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  else R(dir > 0 ? tx : tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);                                                        // the lamps' warm light on the shoulders
  const skinArm = !P.coat;
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, skinArm ? P.skin : P.topD); R(ax, ty + 1, 1, 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.topD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); }
    else { R(sg < 0 ? ax - 4 : ax, ty + 2, 5, 1, P.topD); }
  };
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0) + bend * dir * 2;
    if (!st) { R(ax, ty + 1, 1, th - 2, skinArm ? P.skin : P.topD); R(ax, ty + 1, 1, 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k) + bend * dir, ty + 2 - k, 1, 1, P.topD); R(x + dir * 4 + bend * dir, ty - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0) + bend * dir * 2, hr = hh >= 5 ? 2 : 1, hY = hy + bend;
  if (view === 0) {
    R(hx, hY, hw, hh, P.hair); R(hx, hY, hw, 1, P.hairL);
    if (P.long) R(hx, hY + hh, hw, P.long, P.hair); else R(hx + 1, hY + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hY + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hY + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hY, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hY - 1, hw, 2, P.hat); if (side) R(dir > 0 ? hx + hw : hx - 1, hY, 1, 1, P.hat); }
  return hY;
}
const tyOf = (P, fy) => fy - P.ll - P.th + 1;
/** an umbrella over a head at (x, top), open 0..1 (a furled stick at 0), its canopy colour */
function umbrella(x, top, open, col, hand = 0) {
  x = Math.round(x); top = Math.round(top);
  if (open < 0.08) { R(x + hand + 3, top + 6, 1, 7, '#2a2a30'); return; }
  const hw = Math.round(2 + 4 * open), y0 = top - 4;
  R(x, y0 + 1, 1, 6, '#1a1a20');                                                   // the shaft
  for (let i = -hw; i <= hw; i++) {
    const dy = Math.round((i / hw) ** 2 * 2 * open);
    R(x + i, y0 + dy - 1, 1, 2, i < 0 ? mixHex(col, '#ffffff', 0.12) : col);
  }
  dot(x, y0 - 2, '#1a1a20');
  ctx.globalAlpha = 0.5; for (let i = -hw; i <= hw; i += 2) dot(x + i, y0 + 1 + Math.round((i / hw) ** 2 * 2 * open), '#c8d0e8'); ctx.globalAlpha = 1;   // drops on its rim
}
const L_READER = look(21, 0, 2, '#6a7a5a', '#2a2a34', { coat: 1, long: 2, dk: 0.2 });
const L_VENDOR = look(20, 2, 0, '#f0e8d8', '#2a2a30', { hat: '#5a3424', dk: 0.12 });
const L_PHOTO = look(24, 1, 1, '#2a3a4a', '#1e2028', { coat: 1, hat: '#3a3a3a', dk: 0.3 });
const L_PARENT = look(23, 3, 0, '#8a4a3a', '#2a2a36', { coat: 1, dk: 0.32 }), L_KID = look(14, 3, 0, '#e8c030', '#2a3a6a', { coat: 1, dk: 0.26 });
const L_DOGW = look(25, 0, 3, '#3a5a7a', '#2a2a30', { coat: 1, long: 3, dk: 0.3 });
const L_JOG = look(25, 2, 0, '#e86a3a', '#1a1c28', { shorts: 1, dk: 0.3 });
const L_STROLL_A = look(25, 1, 0, '#4a4a5a', '#2a2a36', { coat: 1, dk: 0.32 }), L_STROLL_B = look(24, 0, 4, '#c87a8a', '#2a2a38', { coat: 1, long: 3, dk: 0.32 });
const CUSTOMERS = [look(24, 0, 1, '#5a7a8a', '#2a2a30', { coat: 1, dk: 0.3 }), look(23, 3, 0, '#c8b898', '#2a3a5a', { dk: 0.3 }),
  look(24, 1, 4, '#7a3a4a', '#2a2a38', { long: 3, coat: 1, dk: 0.3 }), look(25, 2, 0, '#3a5a3a', '#c8b898', { hat: '#1e2e5a', dk: 0.3 }),
  look(23, 0, 3, '#e8e0d0', '#1e2030', { long: 2, dk: 0.3 })];
const L_PAIR_A = look(9, 1, 0, '#3a4a6a', '#1e2030', { dk: 0.25 }), L_PAIR_B = look(8, 0, 3, '#d8a0a8', '#2a2a36', { long: 1, dk: 0.25 });
const UMB = ['#2a4a8a', '#b83a3a', '#2a6a4a', '#d8b83a', '#3a2a4a'];

/* ================= The cast ================= */
const pageTurn = t => frac((t - 4) / 20) < 0.06;                                      // a page turns at 4, 24, 44 ...
const showerOpen = t => smooth(SHOWER.rain0 - 1, SHOWER.full - 2, t) * (1 - smooth(SHOWER.clear - 4, SHOWER.clear + 2, t));
/** how far the umbrellas are open: all loop in a picked wet weather, through the shower in the city's own, never otherwise */
const umbOpen = t => WET_PICK ? 1 : OWN ? showerOpen(t) : 0;
function drawReader(t, rainy) {
  const x = BENCH.x + 18, fy = BENCH.y - 6, bells = inWin(t, STRIKES[0] - 1, STRIKES[3]), um = umbOpen(t);
  const hy = person(x, fy, L_READER, { view: 2, dir: -1, sit: true, aR: bells ? 0 : 2 });
  if (!bells) { R(x - 5, hy + 3, 3, 3, pageTurn(t) ? '#fff8e8' : '#e8dcc0'); dot(x - 3, hy + 3, '#a89a80'); }   // the book, a page turning
  else { R(x - 3, hy + 6, 3, 2, '#d8ccb0'); dot(x - 2, hy - 1, L_READER.hair); }       // the book lowered; looking up at the bells
  if (um > 0.05) umbrella(x + 1, hy, um, UMB[0], -2);
}
const handoff = t => frac((t - 9) / 24);                                               // a slice goes over the counter at 9, 33, 57 ...
function drawVendor(t, rainy) {
  const h = handoff(t), give = h < 0.07 || h > 0.97, x = give ? CART.x0 + 20 : CART.x0 + 12 + Math.round(8 * (0.5 + 0.5 * Math.sin(TAU * frac(t / 8)))), top = CART.top + 7;
  R(x - 3, top + 5, 7, CART.counter - 6 - top - 5, L_VENDOR.top); R(x - 3, top + 5, 7, 1, mixHex(L_VENDOR.top, '#ffe0a0', 0.4));
  R(x - 2, top, 4, 5, L_VENDOR.skin); R(x - 2, top, 4, 2, L_VENDOR.hair); R(x - 2, top - 1, 4, 2, L_VENDOR.hat);
  if (give) { R(x + 3, top + 7, 3, 1, L_VENDOR.skin); R(x + 5, top + 6, 3, 1, '#f0ece0'); dot(x + 6, top + 5, '#e8b850'); }   // a slice on a paper plate
}
function drawFlap(t, rainy) {                                                           // the canopy's front flap, let down in the shower
  const f = umbOpen(t) * 0.6, n = Math.round(f * 7);
  for (let y = 0; y < n; y++) for (let x = CART.x0 - 2; x <= CART.x1 + 2; x++) dot(x, CART.top + 5 + y, ((x - CART.x0 + 40) >> 2) & 1 ? '#c8bca0' : '#4a2a1e');
}
function drawQueue(t, crowd) {
  [[0, RANK.queue], [24, RANK.queue2]].forEach(([off, rank], slot) => {
    if (!here(rank, crowd)) return;
    const q = t - off + 480, u = q % 48, c = (Math.floor(q / 48) + slot * 2) % CUSTOMERS.length, P = CUSTOMERS[c], fy = PATH_Y + slot;
    if (u < 10) { const x = lerp(500, 446, u / 10); person(x, fy, P, { view: 2, dir: -1, step: Math.floor(x / 4) & 1 }); }
    else if (u < 24) person(436, fy, P, { view: u < 18 ? 0 : 2, dir: -1, aR: u > 13 && u < 20 ? 2 : 0 });
    else if (u < 34) person(429, fy, P, { view: 0, aR: u > 32 ? 3 : 0, aL: u > 27 && u < 30 ? 2 : 0 });
    else {
      const x = lerp(429, -20, (u - 34) / 14);
      person(x, fy + 1, P, { view: 2, dir: -1, step: Math.floor(x / 4) & 1, aR: frac(u / 5) < 0.3 ? 2 : 3 });
    }
  });
}
function drawPhotographer(t, rainy) {
  const x = 176, fy = PATH_Y - 3, covered = WET_PICK || (OWN && inWin(t, SHOWER.full - 3, SHOWER.clear)), check = frac(t / 30 + 0.4) < 0.18 && !covered;
  const look2 = inWin(t, STRIKES[0], STRIKES[5]);
  ctx.fillStyle = '#1a1c24';                                                           // the tripod's legs and the camera
  for (let k = 0; k < 10; k++) { dot(x + 6 - k * 0.3, fy - 9 + k, '#1a1c24'); dot(x + 6 + k * 0.45, fy - 9 + k, '#14161c'); }
  R(x + 4, fy - 12, 5, 3, '#22242c'); dot(x + 9, fy - 11, '#3a3c48');
  if (covered) R(x + 3, fy - 13, 7, 4, '#2a3a2a');                                       // a cloth over the camera in the shower
  else if (check) { dot(x + 4, fy - 11, '#9ad0ff'); halo(HL.screen, x + 4, fy - 11, 0.6); }
  person(x, fy, L_PHOTO, { view: look2 ? 1 : 0, aR: check ? 3 : 0, lean: check ? 1 : 0 });
}
function drawKid(t, rainy) {
  const fy = 191, kx = 262, px2 = 272, ducks = inWin(t, DUCKLINGS.t0 + 4, DUCKLINGS.t1 - 6), swans = inWin(t, SWANS.go[1] - 6, SWANS.go[1] + 6);
  const um = umbOpen(t);
  const ph = person(px2, fy, L_PARENT, { view: 0, aR: ducks ? 0 : 0, aL: um > 0.05 ? 1 : 0, lean: ducks ? -1 : 0 });
  if (um > 0.05) umbrella(px2 - 3, ph - 1, um, UMB[1]);
  if (ducks) person(kx, fy, L_KID, { view: 0, crouch: true, aL: 4 });                  // crouched at the edge, pointing at the ducklings
  else if (swans) person(kx, fy, L_KID, { view: 0, aR: 4 });                            // pointing at the swans coming in
  else { const h = hash(step(t, 60), 9); person(kx, fy, L_KID, { view: h < 0.2 ? 1 : 0, aR: h > 0.82 ? 3 : 0, lean: h > 0.5 && h < 0.6 ? 1 : 0 }); }
}
const DOG_ROWS = [
  ['........ee..', '.......edddn', 't......ddd..', '.tlllllllll.', '..dddddddd..', '..d.d..d.d..', '..d.d..d.d..'],
  ['........ee..', '.......edddn', 't......ddd..', '.tlllllllll.', '..dddddddd..', '...dd..dd...', '..d..d.d..d.'],
  ['............', '........ee..', 't......eddd.', '.tllllllldn.', '..dddddddd..', '..d.d..d.d..', '..d.d..d.d..'],
];
const DOG_KEY = { d: '#2a2622', l: '#3e3832', t: '#2a2622', e: '#1a1612', n: '#0a0806' };
function drawDogWalker(t) {
  const { t0, t1, stop } = DOGW;
  if (t < t0 || t >= t1) return;
  const s = along([[t0, 500], [stop[0], 150], [stop[1], 150], [t1, -40]], t), x = Math.round(s.x), dx = x - 16, sniff = inWin(t, stop[0], stop[1]);
  const df = sniff ? 2 : s.moving ? (Math.floor(x / 3) & 1) : 0, fy = PATH_Y + 1;
  ctx.fillStyle = 'rgba(6,8,22,0.32)'; ctx.fillRect(dx - 4, fy + 1, 9, 1);
  ctx.drawImage(DOG_SPR[df][1], dx - 6, fy - 6 - (sniff ? 1 : 0));
  person(x, fy, L_DOGW, { view: sniff ? 0 : 2, dir: -1, step: s.moving && (Math.floor(x / 4) & 1), aR: 3, aL: sniff ? 3 : 0 });
  const hx = x - 4, hy = tyOf(L_DOGW, fy) + 2, cx = dx - 2, cy = fy - 4, n = Math.abs(cx - hx);
  ctx.fillStyle = '#c8a03a';
  for (let k = 1; k < n; k++) ctx.fillRect(hx + Math.sign(cx - hx) * k, Math.round(lerp(hy, cy, k / n) + Math.sin(Math.PI * k / n) * 2), 1, 1);
}
function drawJogger(t) {
  if (t < JOG.t0 || t >= JOG.t1) return;
  const u = (t - JOG.t0) / (JOG.t1 - JOG.t0), x = Math.round(lerp(-20, W + 20, u)), f = Math.floor(x / 5) & 1;
  person(x, PATH_Y + 3 - f, L_JOG, { view: 2, dir: 1, step: f, aR: 4 });
  if (x > 0 && x < W) { dot(x - 1, tyOf(L_JOG, PATH_Y + 3) + 1, '#d8f0ff'); }              // the reflective band on the sleeve
}
function drawStroll(t, rainy) {
  if (t < STROLL.t0 || t >= STROLL.t1) return;
  const x = Math.round(lerp(W + 24, -30, (t - STROLL.t0) / (STROLL.t1 - STROLL.t0))), f = Math.floor(x / 4) & 1, fy = PATH_Y + 4;
  person(x + 8, fy + 1, L_STROLL_B, { view: 2, dir: -1, step: !f });
  const hy = person(x, fy, L_STROLL_A, { view: 2, dir: -1, step: f, aR: 3 });
  if (umbOpen(t) > 0.05) umbrella(x + 4, hy - 1, umbOpen(t), UMB[2]);
}
function drawFarWalkers(t, crowd, rainy) {
  if (here(RANK.farwalk, crowd) && inWin(t, FARWALK.t0, FARWALK.t1)) {               // a walker on the far shore path, a phone's light
    const u = (t - FARWALK.t0) / (FARWALK.t1 - FARWALK.t0), x = Math.round(lerp(14, 150, u)), y = Math.round(keyAt(FAR_KEYS, x)) - 3;
    R(x, y - 6, 2, 3, '#3a3e4a'); R(x, y - 3, 2, 3, '#22242c'); dot(x, y - 7, '#5a4a40'); dot(x + 2, y - 5, '#bfe0ff');
    halo(HL.tiny, x + 2, y - 5, 0.5);
  }
  if (here(RANK.far2, crowd) && inWin(t, 88, 112)) {                                   // someone crossing the bridge in the shower
    const s = lerp(BR_LEN - 1, 1, (t - 88) / 24), [x, y] = brAt(s, 0.3, BR.deck);
    R(x, y - 7, 2, 4, '#4a3a3a'); R(x, y - 3, 2, 3, '#1e2028'); dot(x, y - 8, '#c8a080');
    if (umbOpen(t) > 0.3) { R(x - 2, y - 10, 6, 1, UMB[3]); dot(x, y - 9, '#1a1a20'); }
  }
}
/* the couple on the bridge: they walk on from the west end, stop at the middle, watch the swans and the shower under his
   umbrella, turn to the church at the bells, kiss, and walk off the east end */
function pairAt(t) {
  const { on, stop, off, gone } = BRIDGE_PAIR, mid = (PIER_C[0] + PIER_C[1]) / 2 - 1;
  if (t < on || t >= gone) return null;
  if (t < stop) return { s: lerp(-1, mid, (t - on) / (stop - on)), walk: true };
  if (t < off) return { s: mid, walk: false };
  return { s: lerp(mid, BR_LEN + 1, (t - off) / (gone - off)), walk: true };
}
function drawPair(t, rainy) {
  const P = pairAt(t);
  if (!P) return;
  const kiss = inWin(t, BRIDGE_PAIR.kiss[0], BRIDGE_PAIR.kiss[1]), bells = inWin(t, BELLS.chime[0], STRIKES[STRIKES.length - 1] + 2);
  const [xa, ya] = brAt(P.s, 0.9, BR.deck), [xb, yb] = brAt(P.s + (kiss ? 0.55 : 0.75), 0.9, BR.deck);
  const st = P.walk ? (step(t, 768) & 1) : 0;
  const draw = (x, y, L, lean) => {
    x = Math.round(x); y = Math.round(y);
    R(x - 1, y - L.h + 3, 3, 4, mixHex(L.top, '#ffe0b0', 0.25)); R(x - 1, y - L.h + 3, 3, 1, mixHex(L.top, '#ffe8c0', 0.5));
    R(x - 1, y - 3, st ? 1 : 3, 3, L.bot); if (st) dot(x + 1, y - 1, L.bot);
    R(x - 1 + lean, y - L.h + 1, 2, 2, bells ? L.hair : L.skin); dot(x + lean, y - L.h + 1, L.hair);
  };
  draw(xa, ya, L_PAIR_A, kiss ? 1 : 0); draw(xb, yb, L_PAIR_B, kiss ? -1 : 0);
  if (!P.walk && !kiss && frac(t / 16) < 0.3) dot(Math.round(xb) - 2, Math.round(yb) - 6, L_PAIR_B.skin);   // she points out the swans
  if (umbOpen(t) > 0.3) { const x = Math.round(xa) + 1, y = Math.round(ya) - 12; R(x - 3, y, 7, 1, UMB[4]); R(x - 2, y - 1, 5, 1, UMB[4]); dot(x, y + 1, '#1a1a20'); }
}

const PAIR_A = [], PAIR_B = [];                     // the bridge's couple at every height as they come nearer (looks by height)
for (let h = 9; h <= 24; h++) { PAIR_A[h] = look(h, 1, 0, '#3a4a6a', '#1e2030', { coat: 1, dk: 0.28 }); PAIR_B[h] = look(h, 0, 3, '#d8a0a8', '#2a2a36', { long: h > 14 ? 2 : 1, dk: 0.28 }); }
function drawPairBank(t, crowd) {
  const { gone, down, away } = BRIDGE_PAIR;
  if (!here(RANK.pair, crowd) || t < gone || t >= away) return;
  let x, fy, h, o;
  if (t < down) { const u = (t - gone) / (down - gone); x = lerp(391, 366, u); fy = lerp(164, 198, u); h = Math.round(lerp(10, 23, u)); o = { view: 1, step: step(t, 768) & 1 }; }
  else { const u = (t - down) / (away - down); x = lerp(366, -24, u); fy = 199; h = 23; o = { view: 2, dir: -1, step: step(t, 768) & 1, aR: 5 }; }
  person(x + (t < down ? 5 : 7), fy + 1, PAIR_B[h - 1], { ...o, aR: 0 });             // she walks on his left, a step behind
  person(x, fy, PAIR_A[h], o);
}

/* ================= The garden's birds and beasts ================= */
// the two mute swans: across to the landing (12-52), resting and preening by the boats, back (128-168), asleep by day's end
const SWAN_REST = [[64, 182], [206, 176]];
function swanAt(t, i) {
  const o = i * 7;
  const keys = [[0, 0], [SWANS.go[0] + i, 0], [SWANS.go[1] + i, 1], [SWANS.back[0] + i, 1], [SWANS.back[1] + i, 0], [LOOP, 0]];
  let k = 1;
  while (k < keys.length - 1 && t >= keys[k][0]) k++;
  const [ta, a] = keys[k - 1], [tb, b] = keys[k], u = smooth(0, 1, (t - ta) / (tb - ta));
  const A = SWAN_REST[a], B = SWAN_REST[b], moving = a !== b && u > 0 && u < 1;
  const x = lerp(A[0], B[0], u) + o + (moving ? 0 : 1.5 * wave(t, 6, i * 0.3)), y = lerp(A[1], B[1], u) + Math.sin(Math.PI * u) * (moving ? -6 : 0) + i * 2;
  return { x, y, dir: moving ? Math.sign(B[0] - A[0]) : (i ? -1 : 1), moving, asleep: !moving && a === 0 && (t > SWANS.back[1] + 8 || t < SWANS.go[0] - 2) };
}
const SWAN_ROWS = { up: ['...ww.', '..bkw.', '....w.', '....w.', '.www..', 'wwwww.', '.WWWW.'], asleep: ['......', '......', '......', '......', '.wwww.', 'wwwwww', '.WWWW.'],
  preen: ['......', '......', '......', '....w.', '.wwwkb', 'wwwww.', '.WWWW.'] };
const SWAN_KEY = { w: '#eeece4', W: '#a8a8a8', b: '#d8702a', k: '#141414' };
function drawSwans(t) {
  for (let i = 0; i < 2; i++) {
    const s = swanAt(t, i), x = Math.round(s.x), y = Math.round(s.y), preen = !s.moving && !s.asleep && frac(t / 10 + i * 0.5) < 0.25;
    const spr = SWAN_SPR[s.asleep ? 'asleep' : preen ? 'preen' : 'up'][s.dir > 0 ? 0 : 1];
    ctx.globalAlpha = 0.3; ctx.drawImage(spr, 0, 4, 6, 3, x - 3, y + 1, 6, 3); ctx.globalAlpha = 1;   // its mirror
    ctx.drawImage(spr, x - 3, y - 6);
    if (s.moving) for (let k = 1; k < 9; k++) { ctx.globalAlpha = 0.35 * (1 - k / 9); dot(x - s.dir * (3 + k), y + 1 + k * 0.4, '#8890b8'); dot(x - s.dir * (3 + k), y + 1 - k * 0.15, '#8890b8'); ctx.globalAlpha = 1; }
  }
}
// mallards: a drake and hens dabbling about the lagoon, and two asleep on the finger float
const DRAKE = ['..gg....', '.ggw....', '..wbbbb.', '.bbbbbbt', '..pppp..'], HEN = ['..hh....', '.hhh....', '..hhhhh.', '.hHhhhht', '..hhhh..'];
const DUCK_K = { g: '#1e5a3a', w: '#e8e8e0', b: '#8a8a90', t: '#2a2a30', p: '#6a4a3a', h: '#7a5a3a', H: '#5a3e28' };
const MALLARDS = [{ x: 120, a: 40, k: 1, ph: 0.2, y: 188, drake: true }, { x: 132, a: 34, k: 1, ph: 0.26, y: 190, drake: false }, { x: 30, a: 18, k: 2, ph: 0.7, y: 184, drake: false }];
function drawMallards(t) {
  MALLARDS.forEach((m, i) => {
    const a = TAU * (m.k * t / LOOP + m.ph), x = Math.round(m.x + m.a * Math.sin(a)), y = m.y + Math.round(0.5 * wave(t, 30, i * 0.3)), d = Math.cos(a) > 0 ? 0 : 1;
    const dab = frac(t / (8 + i * 2) + i * 0.3) < 0.12;                                // tail up, head under
    ctx.globalAlpha = 0.25; R(x - 4, y + 1, 9, 1, '#8890b8'); ctx.globalAlpha = 1;
    if (dab) { R(x - 2, y - 1, 5, 2, m.drake ? '#8a8a90' : '#7a5a3a'); dot(d ? x - 3 : x + 3, y - 2, '#2a2a30'); }
    else ctx.drawImage(DUCK_SPR[m.drake ? 0 : 1][d], x - 4, y - 4);
    if (frac(t / 3 + i * 0.4) < 0.5) { ctx.globalAlpha = 0.3; dot(x + (d ? 5 : -5), y + 1, '#c8d0e8'); ctx.globalAlpha = 1; }
  });
  for (const [x, y] of [[PAV.x0 - 14, PAV.float], [PAV.x0 - 9, PAV.float]]) { R(x, y - 2, 4, 2, '#5a4a36'); dot(x + 3, y - 2, '#1e4a30'); }   // asleep on the float
}
// the mallard hen and her eight ducklings, in a line from the landing along the near curb (the bronzes, alive)
function drawDucklings(t) {
  const { t0, t1 } = DUCKLINGS;
  if (t < t0 || t >= t1) return;
  const path = u => { const x = lerp(250, 30, u), y = 182 + 9 * Math.sin(Math.PI * Math.min(1, u * 1.6) / 2); return [x, y]; };
  const u0 = (t - t0) / (t1 - t0);
  const fade = smooth(t0, t0 + 2, t) * (1 - smooth(t1 - 2, t1, t));
  ctx.globalAlpha = fade;
  for (let k = 0; k < 9; k++) {
    const u = u0 - k * 0.018 + (k ? 0.004 * Math.sin(TAU * frac(t / 1.6 + k * 0.37)) : 0);
    if (u < 0 || u > 1) continue;
    const [x, y] = path(u), xr = Math.round(x), yr = Math.round(y);
    if (k === 0) ctx.drawImage(DUCK_SPR[1][1], xr - 4, yr - 4);
    else { R(xr, yr - 1, 2, 1, '#b8a050'); dot(xr, yr - 2, '#6a5a2a'); dot(xr + 1, yr, '#5a4a2a'); }
    ctx.globalAlpha = 0.25 * fade; dot(xr + 3, yr + 1, '#c8d0e8'); ctx.globalAlpha = fade;
  }
  ctx.globalAlpha = 1;
}
// the black-crowned night heron on the curb at the left: hunched, then the neck shoots out
const HERON_ROWS = { stand: ['..kk...', '.kkkk..', 'wwkkkk.', '.wkggg.', '.wwggg.', '..wgg..', '...y...', '..y.y..'],
  strike: ['.......', '.......', '...kkk.', '..kkggg', '.wkggg.', 'wkw.g..', 'kw.y...', '..y.y..'] };
const HERON_K = { k: '#161a24', g: '#6a6e7c', w: '#c8c8c4', y: '#c0a030' };
const HERON_AT = { x: 22, y: CURB - 7 };
function drawHeron(t) {
  const strike = HERON.some(s => inWin(t, s, s + 1.6)), { x, y } = HERON_AT;
  ctx.drawImage(strike ? HERON_SPR.strike : HERON_SPR.stand, x, y);
  const s0 = HERON.find(s => inWin(t, s + 0.6, s + 2.6));
  if (s0 !== undefined) ring(x - 1, CURB - 4, (t - s0 - 0.6) / 2, 6, 0.45);
}
// a cottontail on the east lawn by the tulips: nibbling, ears up, a hop now and then
function drawRabbit(t) {
  const hop = frac(t / 30 + 0.3), x = 356 + (hop > 0.5 ? 3 : 0) + (hop > 0.48 && hop < 0.5 ? 1 : 0), y = 189 - (hop > 0.48 && hop < 0.5 ? 2 : 0);
  const ears = frac(t / 6) < 0.3;
  R(x, y - 2, 4, 2, '#4a3e32'); R(x + 3, y - 3, 2, 2, '#4a3e32'); dot(x, y - 2, '#c8c0b0');
  if (ears) { dot(x + 4, y - 5, '#3a3028'); dot(x + 4, y - 4, '#3a3028'); } else dot(x + 4, y - 4, '#3a3028');
}
// little brown bats over the water, hawking the insects the rain brought out
const BATS = [{ cx: 120, cy: 128, ax: 70, ay: 14, kx: 7, ky: 17, ph: 0.1 }, { cx: 260, cy: 118, ax: 50, ay: 12, kx: 9, ky: 19, ph: 0.45 },
  { cx: 180, cy: 140, ax: 56, ay: 9, kx: 8, ky: 21, ph: 0.8 }];
function drawBats(t) {
  BATS.forEach((b, i) => {
    const x = Math.round(b.cx + b.ax * Math.sin(TAU * (b.kx * t / LOOP + b.ph)) + 7 * Math.sin(TAU * (b.kx * 5 * t / LOOP + i))), y = Math.round(b.cy + b.ay * Math.sin(TAU * (b.ky * t / LOOP + b.ph * 2)));
    ctx.fillStyle = '#05060f';
    if ((step(t, 2880) + i) & 1) { ctx.fillRect(x - 1, y - 1, 1, 1); ctx.fillRect(x + 1, y - 1, 1, 1); ctx.fillRect(x, y, 1, 1); } else ctx.fillRect(x - 1, y, 3, 1);
  });
}
/** a ring spreading on the water at (x, y): u 0..1 of its life, r its last radius, a its alpha */
function ring(x, y, u, r, a) {
  if (u < 0 || u >= 1) return;
  const rr = 0.8 + u * r, n = Math.max(6, Math.round(rr * 3));
  ctx.globalAlpha = a * (1 - u); ctx.fillStyle = '#b8c4e0';
  for (let j = 0; j < n; j++) { const an = j / n * TAU; ctx.fillRect(Math.round(x + Math.cos(an) * rr), Math.round(y + Math.sin(an) * rr * 0.32), 1, 1); }
  ctx.globalAlpha = 1;
}

/* ================= Prerendered state ================= */
let GRASS_TEX, LAWN_MASK, GRASS_SCR, HL, SKY_C, MOON_C, CLOUDS, DECK_C, FAR_C, TREES_C, BRIDGE, PAV_C, BOAT_SPR, WATER_C, MIRROR_C, GROUND_C, LAMP_C, BENCH_C,
  WILLOW_C, HAWTHORN_C, CART_C, BOARD_C, AD_C, PITCH, DOG_SPR, DUCK_SPR, HERON_SPR, SWAN_SPR, VIG_C, HAN_CLOUD_C, HAN_MASK_C, HAN_TMP, PAV_REF;
const SPLIT = {};
const TREE_TIPS = [];                               // leaves at the tree line's edge that stir
const LAMP_ROWS = [];                               // per bridge lamp: the water rows its reflection may use [x, y, ra, rb]
function prerender() {
  for (const L of [WINS, TREE_TIPS, LAMP_ROWS, STRANDS, FAR_STRANDS]) L.length = 0;
  HL = {
    city: makeHalo(90, '#ffb878', 0.05, 1.5), moon: makeHalo(26, '#d8dcf0', 0.12, 1.6), brLamp: makeHalo(9, '#fff0c8', 0.36, 1.3),
    brWide: makeHalo(24, '#ffd890', 0.09, 1.6), lampN: makeHalo(40, '#ffc878', 0.16, 1.7), lampCore: makeHalo(9, '#fff2d0', 0.4, 1.3),
    cart: makeHalo(26, '#ffc070', 0.18, 1.5), sign: makeHalo(22, '#ffe2b0', 0.14, 1.5), pav: makeHalo(14, '#ffd8a0', 0.26, 1.4),
    far: makeHalo(4, '#ffe2a8', 0.4, 1.2), beacon: makeHalo(7, '#4a7cff', 0.5, 1.2), beaconR: makeHalo(7, '#ff3a30', 0.5, 1.2),
    red: makeHalo(3, '#ff3a30', 0.5, 1), tiny: makeHalo(3, '#fff4d0', 0.5, 1.1), screen: makeHalo(4, '#9ad0ff', 0.4, 1.2),
    bell: makeHalo(11, '#ffd8a0', 0.22, 1.3), steeple: makeHalo(12, '#ffb870', 0.12, 1.5), taj: makeHalo(28, '#ffd090', 0.05, 1.5),
  };
  SKY_C = renderSky(); MOON_C = renderMoon();
  CLOUDS = [makeClouds(31, 70, 7, 46, 9, ['#1a1d34', '#262a46', '#4a4060'], 0.9), makeClouds(57, 64, 6, 60, 11, ['#141729', '#20233c', '#3e3654'], 0.85),
    makeClouds(83, 40, 9, 30, 6, ['#101322', '#1a1d30', '#30304a'], 0.8)];
  DECK_C = makeDeck();
  const r = rng(2026);
  const layer = fn => { const p = pix(W, H); fn(p); return trim(p); };
  SPLIT.far = WINS.length;
  FAR_C = layer(p => { paintBerkeley(p, r); paintHancock(p, r); paintBoylston(p, r); paintSteeple(p); paintCarlton(p, r); paintTaj(p, r); });
  HAN_CLOUD_C = makeHanClouds(); HAN_MASK_C = makeHanMask(); HAN_TMP = makeCanvas(W, HAN_H);
  SPLIT.trees = WINS.length;
  TREES_C = layer(p => { paintTrees(p); paintShore(p); paintStatue(p); });
  for (let x = 0; x < W; x++) for (let k = 0; k < 7; k++) if (hash(x, k, 451) < 0.42) {
    const y = TREE_TOP[x] + k * 4 + Math.floor(hash(x, k, 452) * 4);
    if (y >= FAR[x] - 1) continue;
    TREE_TIPS.push({ x, y, ph: hash(x, k, 453), k: 20 + Math.floor(hash(x, k, 454) * 40), col: pick(TREE, 0.5 + hash(x, k, 455) * 0.3, x, y) });
  }
  BRIDGE = renderBridge();
  PAV_C = renderPavilion();
  placeBoats();
  BOAT_SPR = [sprite(BOAT_ROWS, BOAT_KEY), sprite(BOAT_ROWS, BOAT_KEY_FAR)];
  buildMask();
  WATER_C = renderWater();
  {
    const [c, x] = makeCanvas(W, H);
    x.drawImage(SKY_C, 0, 0);
    for (const L of [FAR_C, TREES_C, BRIDGE.back, BRIDGE.front]) x.drawImage(L.c, L.x, L.y);
    for (const L of BR_LAMPS) { x.fillStyle = '#fff4d8'; x.fillRect(L.x - 1, L.y - 1, 2, 2); }
    MIRROR_C = renderMirror(c);
  }
  for (const L of BR_LAMPS) {                                                          // where each lamp's double can show
    const wl = BR_WL[L.x] || FAR[L.x], ry = Math.round(2 * wl - L.y);
    let ra = ry - 3, rb = ry + 7;
    while (ra < rb && !isWater(L.x, ra)) ra++;
    while (rb > ra && !isWater(L.x, rb)) rb--;
    if (rb > ra) LAMP_ROWS.push([L.x, ry, ra, rb + 1, L.far]);
  }
  for (let i = 0; i < 160; i++) {                                                      // where drips and raindrops ring the water
    const x = Math.floor(hash(i, 1, 801) * W), y = 158 + Math.floor(hash(i, 2, 801) * (CURB - 158));
    if (isWater(x, y) && isWater(x - 3, y) && isWater(x + 3, y)) RING_SPOTS.push([x, y]);
  }
  GROUND_C = renderGround();
  GRASS_TEX = renderGrassTex(); GRASS_SCR = makeCanvas(W, H - GRASS_Y);
  { const p = pix(W, H - GRASS_Y); for (let y = GRASS_Y; y < H; y++) for (let x = 0; x < W; x++) if (LAWN_PX[y * W + x]) p.set(x, y - GRASS_Y, "#ffffff"); LAWN_MASK = p.done(); } LAMP_C = renderLamp(); BENCH_C = renderBench(); WILLOW_C = renderWillowTrunk(); HAWTHORN_C = renderHawthorn();
  CART_C = renderCart(); BOARD_C = renderBoard(); AD_C = renderAd();
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  DOG_SPR = DOG_ROWS.map(rows => [sprite(rows, DOG_KEY), sprite(rows, DOG_KEY, true)]);
  DUCK_SPR = [[sprite(DRAKE, DUCK_K, true), sprite(DRAKE, DUCK_K)], [sprite(HEN, DUCK_K, true), sprite(HEN, DUCK_K)]];
  HERON_SPR = { stand: sprite(HERON_ROWS.stand, HERON_K), strike: sprite(HERON_ROWS.strike, HERON_K) };
  SWAN_SPR = Object.fromEntries(Object.entries(SWAN_ROWS).map(([k, rows]) => [k, [sprite(rows, SWAN_KEY, true), sprite(rows, SWAN_KEY)]]));
  VIG_C = renderVignette();
}

/* ================= Per-frame: the sky ================= */
let OWN = true, WET_PICK = false;                  // set by renderFrame from the weather on show: the city's own, a picked wet one
const covNow = t => WET_PICK ? 0.9 : OWN ? coverAt(t) : 0;
/** how much rain rings the water and roughens it: the shower in the city's own, all loop in a picked wet weather */
const ringAmt = t => WET_PICK ? 1 : OWN ? showerAt(t) : 0;
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  const cov = covNow(t), clear = 1 - cov;
  for (const s of STARS) { ctx.globalAlpha = clear * s.b * (0.4 + 0.6 * Math.max(0, wave(t, s.k, s.ph))); ctx.fillStyle = s.c; ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = clear;
  halo(HL.moon, MOON.x, MOON.y, clear * 0.9);
  ctx.globalAlpha = clear; ctx.drawImage(MOON_C, MOON.x - MOON.r - 1, MOON.y - MOON.r - 1); ctx.globalAlpha = 1;
  CLOUDS.forEach((c, i) => { const off = scroll(t, i + 1, W), y = [4, 28, 70][i]; ctx.drawImage(c, off - W, y); ctx.drawImage(c, off, y); });
  if (cov > 0.01) {                                                                    // the shower's cloud bank slides in from the west and on east
    const x = WET_PICK ? -80 : Math.round(lerp(-W - 160, W, smooth(SHOWER.t0 - 6, SHOWER.t1 + 10, t)));
    ctx.globalAlpha = Math.min(1, cov * 1.2); ctx.drawImage(DECK_C, x, -4); ctx.globalAlpha = 1;
    ctx.globalAlpha = cov * 0.35; ctx.fillStyle = '#0c0f22'; ctx.fillRect(0, 0, W, 130); ctx.globalAlpha = 1;
  }
  for (const J of JETS_HI) {                                                           // an airliner high over, its lamps blinking
    if (t < J.t0 || t >= J.t1) continue;
    const u = (t - J.t0) / (J.t1 - J.t0), x = Math.round(lerp(-10, W + 10, u)), y = Math.round(lerp(22, 12, u));
    dot(x, y, '#c8ccd8'); if (frac(t * 0.9) < 0.1) dot(x, y + 1, '#ff4a40'); if (frac(t * 0.7 + 0.3) < 0.06) dot(x + 1, y, '#ffffff');
  }
}
const winLit = (w, t) => frac(w.k * t / LOOP + w.ph) < w.duty;
function drawWins(a, b, t) { for (let i = a; i < b; i++) { const w = WINS[i]; if (winLit(w, t)) R(w.x, w.y, w.w, w.h, w.c); } }
/* the Berkeley beacon: steady blue, clear view; flashing blue, clouds due; steady red, rain ahead; flashing red, snow
   instead. It forecasts the weather on show: the city's own clears after the shower */
function drawBeacon(t, weather) {
  const wet = weather === 'drizzle' || weather === 'rain' || weather === 'thunderstorm', snow = weather === 'snow';
  const red = wet || snow, on = !snow || frac(t / 2) < 0.6, col = red ? '#ff4a3a' : '#5a8cff', y = BER.mast;
  if (!on) { R(BER.x - 1, y, 2, 3, red ? '#4a1a1a' : '#1a2a5a'); return; }
  R(BER.x - 1, y + 1, 2, 2, col); dot(BER.x, y, red ? '#ffb0a0' : '#c8dcff'); dot(BER.x - 1, y + 3, mixHex(col, '#000000', 0.4));
  halo(red ? HL.beaconR : HL.beacon, BER.x, y + 1, 0.9);
}
/** the clouds drifting across the Hancock's glass, one tile's width a loop, dimmed under the shower's cloud bank */
function drawHancockClouds(t) {
  const [c, x] = HAN_TMP, off = scroll(t, 1, HAN_TW);
  x.globalCompositeOperation = 'source-over'; x.clearRect(0, 0, W, HAN_H);
  x.drawImage(HAN_CLOUD_C, HAN.x0 + 1 + off - HAN_TW, 0); x.drawImage(HAN_CLOUD_C, HAN.x0 + 1 + off, 0);
  x.globalCompositeOperation = 'destination-in'; x.drawImage(HAN_MASK_C, 0, 0); x.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 0.8 * (1 - covNow(t) * 0.6); ctx.drawImage(c, 0, HAN.top + 2); ctx.globalAlpha = 1;
}
function drawFar(t, weather) {
  halo(HL.city, 70, 150, 1); halo(HL.city, 190, 156, 0.6);
  blit(FAR_C); drawWins(SPLIT.far, SPLIT.trees, t);
  drawHancockClouds(t);
  const b = beacon(t);
  for (const [x, y] of [[HAN.x0 + 1, HAN.top], [HAN.notch - 3, HAN.top], [BER.x0 + 3, BER.top + 2], [TAJ.x0 + 2, TAJ.top]]) { ctx.globalAlpha = 0.35 + 0.65 * b; dot(x, y, '#ff3a30'); ctx.globalAlpha = 1; halo(HL.red, x, y, b * 0.8); }
  drawBeacon(t, weather);
  halo(HL.steeple, STEEPLE.x, STEEPLE.tip + 16, 0.8);
  const sk = STRIKES.find(s => inWin(t, s, s + 2));                                    // each bell stroke warms the belfry a little
  if (sk !== undefined) halo(HL.bell, STEEPLE.x, STEEPLE.tip + 21, 0.7 * (1 - (t - sk) / 2));
  halo(HL.taj, (TAJ.x0 + TAJ.x1) / 2, 104, 0.8);
  blit(TREES_C); drawWins(SPLIT.trees, WINS.length, t);
  const br = breezeAt(t);
  for (const m of TREE_TIPS) if (wave(t, m.k, m.ph) > 0.74 - br * 0.5) dot(m.x + (br > 0.3 ? -1 : 0), m.y, m.col);   // leaves stirring, more in the breeze
  for (const s of FAR_STRANDS) { const sw = Math.round(swayAt(t, s.ph) * 1.2); if (sw) { ctx.fillStyle = s.col; ctx.fillRect(s.x + sw, s.y0 + 2, 1, s.y1 - s.y0 - 2); } }
  for (const [x, y] of FAR_LAMPS) { R(x - 1, y - 1, 2, 2, '#fff0c8'); halo(HL.far, x, y, 0.85); }
}
/* ================= Per-frame: the bridge ================= */
function drawBridge(t, crowd, rainy) {
  blit(BRIDGE.back);
  if (here(RANK.pair, crowd)) drawPair(t, rainy);
  blit(BRIDGE.front);
  for (const L of BR_LAMPS) {
    const I = 0.92 + 0.05 * wave(t, 600, L.x * 0.01);
    R(L.x - 1, L.y - 1, 2, 2, L.far ? '#f0e4c8' : '#fff8e4');
    halo(HL.brLamp, L.x, L.y, I * (L.far ? 0.7 : 1)); if (!L.far) halo(HL.brWide, L.x, L.y + 2, I);
  }
}
/* ================= Per-frame: the lagoon ================= */
// the mirror rippled row by row; the breeze's cat's paws break it up as they race across from the landing
function drawWater(t) {
  ctx.drawImage(WATER_C, 0, 0);
  const br = breezeAt(t), front = lerp(W + 40, -60, smooth(BREEZE.t0, BREEZE.gust + 6, t)), wet = ringAmt(t);
  for (let y = 150; y <= CURB; y++) {
    const k = y - 150;
    const amp = 0.4 + k * 0.03 + br * 1.6 + wet * 0.5;
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * amp + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * (0.5 + br));
    ctx.globalAlpha = (0.92 - br * 0.3 - wet * 0.15) * (0.86 + 0.14 * Math.sin(TAU * 60 * t / LOOP + k * 1.7));
    ctx.drawImage(MIRROR_C, 0, y, W, 1, off, y, W, 1);
  }
  ctx.globalAlpha = 1;
  for (const g of GLINTS) {
    const v = wave(t, g.kf, g.ph);
    if (v < -0.1) continue;
    const x = Math.round(frac(g.x + g.k * t / LOOP) * (W + 30)) - 15;
    if (!isWater(x, g.y)) continue;
    ctx.globalAlpha = (0.4 + br * 0.3) * Math.min(1, v + 0.1); ctx.fillStyle = g.c; ctx.fillRect(x, g.y, g.len, 1);
  }
  ctx.globalAlpha = 1;
  if (br > 0.02) {                                                                    // the cat's paws: dark ruffled patches racing left
    for (let i = 0; i < 70; i++) {
      const x = Math.round(front + hash(i, 1, 811) * 260 - 40 + (t - BREEZE.t0) * -2), y = 158 + Math.floor(hash(i, 2, 811) * 38);
      if (!isWater(x, y)) continue;
      ctx.globalAlpha = br * 0.5; R(x, y, 2 + Math.floor(hash(i, 3, 811) * 4), 1, step(t, 2400) + i & 1 ? '#03050f' : '#4a5480'); ctx.globalAlpha = 1;
    }
  }
  for (const [x, ry, ra, rb, far] of LAMP_ROWS) {                                    // the bridge's lamps, doubled
    streak(ctx, t, x, far ? 2 : 3, '#ffe2a8', (far ? 0.3 : 0.5) * (1 - br * 0.4), x, ra, rb);
    if (ry >= ra && ry < rb) { ctx.globalAlpha = (far ? 0.45 : 0.75) * (0.75 + 0.25 * wave(t, 90, x * 0.1)) * (1 - br * 0.5); dot(x + Math.round(wave(t, 45, x * 0.03) * (0.5 + br)), ry, '#fff0c8'); ctx.globalAlpha = 1; }
  }
  for (const [x, y] of FAR_LAMPS) streak(ctx, t, x, 2, '#ffe2a8', 0.14, y, Math.ceil(FAR[x]) + 1, Math.min(CURB, Math.ceil(FAR[x]) + 14));
  streak(ctx, t, (TAJ.x0 + TAJ.x1) / 2, 14, '#ffc880', 0.05, 5, 163, 190);           // the hotels' windows, long and faint
  streak(ctx, t, HAN.x0 + 16, 10, '#8a9ac8', 0.05, 6, 160, 186);
  streak(ctx, t, LAMP_N.x, 4, '#ffd890', 0.2, 8, CURB - 10, CURB + 1);
  drawRings(t);
}
function drawRings(t) {
  // drips off the willows along the far shore
  const WILLOW_DRIP = [[6, 155], [14, 155], [66, 158], [74, 159], [116, 163], [126, 163], [172, 161], [184, 161], [220, 161], [232, 161], [400, 157], [410, 157]];
  const extra = WET_PICK ? 0.6 : OWN ? smooth(SHOWER.thin, SHOWER.t1 + 10, t) * (1 - smooth(150, 175, t)) : 0;   // heavier just after the shower
  WILLOW_DRIP.forEach(([x, y], i) => {
    for (let j = 0; j < 2; j++) {
      const { cyc, u } = cycle(t, 60 + i * 4 + j * 26, i * 0.37 + j * 0.5);
      if (hash(i * 3 + j, cyc, 821) < (j ? extra * 0.8 : 0.6)) ring(x + Math.round((hash(i * 3 + j, cyc, 822) - 0.5) * 6), y + 1, u * 1.6, 4, 0.5);
    }
  });
  // raindrops over the whole lagoon while the shower falls (all loop in a picked rain)
  const wet = ringAmt(t);
  if (wet > 0.01) for (let i = 0; i < RING_SPOTS.length; i++) {
    const { cyc, u } = cycle(t, 160 + (i % 7) * 4, hash(i, 5, 831));
    if (hash(i, cyc, 832) > wet * 0.9) continue;
    const [x0, y0] = RING_SPOTS[i], x = x0 + Math.round((hash(i, cyc, 833) - 0.5) * 30), y = y0 + Math.round((hash(i, cyc, 834) - 0.5) * 6);
    if (isWater(x, y)) ring(x, y, u * 1.4, 2 + (y - 158) * 0.08, 0.55);
  }
  // a fish rising mid-lagoon, frogs plopping off the far edge
  { const { cyc, u } = cycle(t, 18, 0.2); const x = 70 + Math.floor(hash(cyc, 1, 841) * 160), y = 170 + Math.floor(hash(cyc, 2, 841) * 18); if (isWater(x, y)) ring(x, y, u * 2.2, 7, 0.45); }
  { const { cyc, u } = cycle(t, 26, 0.6); const sp = [[40, 157], [104, 163], [150, 163], [196, 162]][cyc % 4]; if (u < 0.05) dot(sp[0], sp[1] - 1, '#3a5a2a'); ring(sp[0], sp[1] + 1, (u - 0.05) * 2.5, 4, 0.5); }
}
/* the petals the breeze takes off the hawthorn: they fly out over the water, settle and drift away left */
const PETALS = Array.from({ length: 44 }, (_, i) => ({ t0: BREEZE.gust + hash(i, 1, 851) * 18, fly: 3 + hash(i, 2, 851) * 4,
  x0: HAWTHORN.x - 10 + hash(i, 3, 851) * 22, y0: HAWTHORN.y - 14 + hash(i, 4, 851) * 12, x1: 150 + hash(i, 5, 851) * 200, y1: 168 + hash(i, 6, 851) * 26,
  ph: hash(i, 7, 851), near: i % 9 === 0 }));
function drawPetals(t, nearOnly) {
  if (t < BREEZE.gust || t >= BREEZE.petals) return;
  for (const P of PETALS) {
    if (t < P.t0 || P.near !== nearOnly) continue;
    const u = (t - P.t0) / P.fly;
    let x, y, a = 1;
    if (P.near) {                                                                       // a few blow past close by, big
      if (u > 1.6) continue;
      x = lerp(P.x0, P.x0 - 260, u / 1.6); y = lerp(P.y0, P.y0 + 70, u / 1.6) + 4 * Math.sin(TAU * (u * 2 + P.ph));
      R(x, y, 2, 2, (step(t, 1200) + P.ph * 10) & 1 ? '#f4e8ec' : '#d8c8d0'); continue;
    }
    if (u < 1) { x = lerp(P.x0, P.x1, u); y = lerp(P.y0, P.y1, u) - Math.sin(Math.PI * u) * 10 + 2 * Math.sin(TAU * (u * 3 + P.ph)); }
    else { x = P.x1 - (t - P.t0 - P.fly) * 0.9; y = P.y1; a = 1 - smooth(BREEZE.petals - 10, BREEZE.petals - 1, t); if (!isWater(x, y)) continue; }
    ctx.globalAlpha = a; dot(x, y, u < 1 && (step(t, 1200) + P.ph * 10) & 1 ? '#d8c8d0' : '#f4e8ec'); ctx.globalAlpha = 1;
  }
}
/* the mist the rain left: thin bands breathing over the water and the far lawns */
function drawMist(t) {
  for (let k = 0; k < 3; k++) {
    const y = 152 + k * 9 + Math.round(1.5 * wave(t, 4, k * 0.3)), a = 0.06 + 0.04 * wave(t, 3 + k, k * 0.21), off = scroll(t, k + 1, W);
    ctx.globalAlpha = a; ctx.fillStyle = '#8a92b8';
    for (let i = 0; i < 9; i++) { const x = ((i * 61 + off + k * 23) % (W + 60)) - 30; ctx.fillRect(x, y, 40 + (i % 3) * 14, 2 + (i & 1)); }
    ctx.globalAlpha = 1;
  }
}
/* ================= Per-frame: the landing ================= */
function drawLanding(t) {
  const br = breezeAt(t);
  BOAT_SCR.forEach((b, i) => {                                                         // the outer boats first, they're further off
    if (!b.outer) return;
    const bob = Math.round(0.5 * wave(t, 24 + i * 3, i * 0.21) + br * 0.8 * wave(t, 120, i * 0.3));
    ctx.drawImage(BOAT_SPR[1], b.x + 1, b.y - 11 + bob);
  });
  BOAT_SCR.forEach((b, i) => {
    if (b.outer) return;
    const bob = Math.round(0.5 * wave(t, 24 + i * 3, i * 0.21) + br * 0.8 * wave(t, 120, i * 0.3));
    ctx.save(); ctx.globalAlpha = 0.28; ctx.translate(b.x, b.y + bob); ctx.scale(1, -1); ctx.drawImage(BOAT_SPR[0], 0, -11); ctx.restore();   // its mirror
    ctx.drawImage(BOAT_SPR[0], b.x, b.y - 11 + bob);
    ctx.globalAlpha = 0.6; ctx.fillStyle = '#5a5850';                                  // the bow line to the float
    for (let k = 0; k < 8; k++) ctx.fillRect(b.x + 54 + k, b.y - 3 + bob + Math.round(k * 0.4), 1, 1);
    ctx.globalAlpha = 1;
  });
  for (let k = 0; k < 8; k++) {                                                        // the float's double, barely rippling
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55 + 1.3) * (0.3 + k * 0.12 + br * 1.2));
    ctx.drawImage(PAV_REF, 0, k, W, 1, off, PAV_WL + 1 + k, W, 1);
  }
  blit(PAV_C);
  const lx = PAV.x0 + 16, ly = PAV.eave + 3;                                           // the lantern under the eave
  dot(lx, ly, '#ffe8b0'); dot(lx, ly + 1, '#f0c070'); halo(HL.pav, lx, ly + 1, 0.85 + 0.08 * wave(t, 300));
  const fx = PAV.x1 + 3, fy = PAV.float - 25, flow = 0.4 + br;                         // the flag, stirring, streaming in the breeze
  for (let i = 1; i <= 7; i++) for (let j = 0; j < 5; j++) {
    const dy = Math.round(Math.sin(TAU * (i / 7 * 0.8 - 6 * t / LOOP * 4) + j * 0.2) * flow * (i / 7));
    const col = i <= 3 && j <= 2 ? '#2a3a6a' : j % 2 ? '#e8e0d8' : '#a8302a';
    dot(fx + i, fy + j + dy + Math.round((1 - flow) * i * 0.4), mixHex(col, '#0a0e22', 0.3));
  }
}
/* ================= Per-frame: the bank, the path, the near things ================= */
const BB_FLICK = [[47.2, 48.4], [171.6, 172.3]];   // one of the sign's lamps stutters now and then
function drawBoard(t, pitch) {
  const fl = BB_FLICK.some(([a, b]) => t >= a && t < b) && hash(step(t, 2400), 13) < 0.5;
  halo(HL.sign, BB.x + 23, BB.y + 9, fl ? 0.4 : 0.7);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(AD_C, BB.x, BB.y);
    if (fl) { ctx.globalAlpha = 0.3; R(BB.x + BB.w / 2, BB.y, BB.w / 2, BB.h, '#0a0c18'); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const [lx, on] of [[BB.x + 12, true], [BB.x + BB.w - 7, !fl]]) { dot(lx, BB.y - 5, on ? '#fff6dc' : '#6a6050'); if (on) halo(HL.tiny, lx, BB.y - 4, 0.9); }
}
function drawWillow(t) {
  blit(WILLOW_C);
  for (const s of STRANDS) {
    const sw = swayAt(t, s.ph);
    for (const [k, n] of s.runs) {
      const u = (k + n / 2) / s.len;
      ctx.fillStyle = u > 0.8 ? s.tip : s.col;
      ctx.fillRect(Math.round(s.x + sw * u * u * 1.4 + Math.sin(k * 0.08 + s.curl) * 0.8), s.y0 + k, 1, n);
    }
  }
}
function drawBank(t, crowd, pitch, billboard, rainy) {
  const DL = [], put = (fy, fn) => DL.push([fy, fn]);
  put(HAWTHORN.y + 8, () => blit(HAWTHORN_C));
  put(189, () => drawRabbit(t));
  if (here(RANK.kid, crowd)) put(191, () => drawKid(t, rainy));
  put(CURB, () => drawHeron(t));
  put(CART.base, () => {
    halo(HL.cart, (CART.x0 + CART.x1) / 2, CART.counter - 4, 0.75);
    if (here(RANK.vendor, crowd)) drawVendor(t, rainy);
    blit(CART_C);
    drawFlap(t, rainy);
    if (Math.round(umbOpen(t) * 0.6 * 7) === 0) dot((CART.x0 + CART.x1) / 2, CART.top + 5, '#fff0c8');   // the bulb, hung right under the canopy (behind the flap)
    if (billboard) drawBoard(t, pitch);
  });
  if (here(RANK.photo, crowd)) put(PATH_Y - 3, () => drawPhotographer(t, rainy));
  put(LAMP_FOOT, () => ctx.drawImage(LAMP_C, LAMP_N.x - 4, LAMP_N.y - 3));
  put(BENCH.y, () => { if (here(RANK.reader, crowd)) drawReader(t, rainy); ctx.drawImage(BENCH_C, BENCH.x, BENCH.y - 12); });
  put(PATH_Y + 0.5, () => drawQueue(t, crowd));
  put(t < BRIDGE_PAIR.down ? lerp(164, 198, clamp((t - BRIDGE_PAIR.gone) / (BRIDGE_PAIR.down - BRIDGE_PAIR.gone), 0, 1)) : 199, () => drawPairBank(t, crowd));
  if (here(RANK.dog, crowd)) put(PATH_Y + 1, () => drawDogWalker(t));
  if (here(RANK.jog, crowd)) put(PATH_Y + 3, () => drawJogger(t));
  if (here(RANK.stroll, crowd)) put(PATH_Y + 4, () => drawStroll(t, rainy));
  put(WILLOW_R.base, () => drawWillow(t));
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
  const I = 0.94 + 0.04 * wave(t, 900, 0.3);
  halo(HL.lampN, LAMP_N.x, LAMP_N.y + 3, I); halo(HL.lampCore, LAMP_N.x, LAMP_N.y, I);
  drawMoths(t);
}
function drawMoths(t) {                                                                // moths round the lamp and the cart's light
  [[LAMP_N.x, LAMP_N.y], [(CART.x0 + CART.x1) / 2, CART.top + 8]].forEach(([lx, ly], li) => {
    for (let k = 0; k < 3; k++) {
      const a = TAU * ((36 + k * 12 + li * 6) * t / LOOP + k / 3 + li * 0.2), r = 4 + k * 2 + Math.sin(TAU * (7 * t / LOOP + k)) * 1.5;
      ctx.globalAlpha = 0.75; dot(lx + r * Math.cos(a), ly - 1 + r * 0.7 * Math.sin(a * 1.3), '#f0e6cc'); ctx.globalAlpha = 1;
    }
  });
}
// the puddles on the path hold the lamps
function drawPuddles(t) {
  streak(ctx, t, LAMP_N.x, 5, '#ffd890', 0.3, 12, CURB + 4, LAWN);                // the lamp's long reflection down the wet path
  for (const [x, y, hw] of PUDDLES) {
    const near = Math.abs(x - LAMP_N.x) < 60 || Math.abs(x - (CART.x0 + CART.x1) / 2) < 40;
    if (!near) continue;
    ctx.globalAlpha = 0.5 + 0.2 * wave(t, 40, x * 0.01); R(x - 1 + Math.round(wave(t, 30, x)), y, 2, 1, '#ffe2a8'); ctx.globalAlpha = 1;
  }
}

/* the nearer tulips nod in the air and bend in the breeze */
function drawTulips(t) {
  const br = breezeAt(t);
  for (const T of TULIPS) {
    const sw = Math.round(0.45 * wave(t, 24, T.ph) + br * (1.4 + 0.6 * wave(t, 96, T.ph)) * (T.tall ? 1.4 : 1));
    ctx.fillStyle = "#1e3a1c"; ctx.fillRect(T.x, T.y - T.tall, 1, 1 + T.tall);
    ctx.fillStyle = T.head; ctx.fillRect(T.x + (sw < 0 ? -1 : sw > 0 ? 1 : 0) * (Math.abs(sw) > 0 ? 1 : 0), T.y - 1 - T.tall, 1, 1);
    if (Math.abs(sw) > 1) { ctx.fillStyle = "#1e3a1c"; ctx.fillRect(T.x + Math.sign(sw), T.y - T.tall, 1, 1); }
  }
}
/* the breeze running through the near lawn in slow waves, much stronger in the gust */
const GRASS_Y = 152;
function renderGrassTex() {                          // light rolling over the wet grass in long diagonal bands (tileable in x)
  const p = pix(W, H - GRASS_Y);
  for (let y = 0; y < H - GRASS_Y; y++) for (let x = 0; x < W; x++) {
    const v = 0.5 + 0.5 * Math.sin(TAU * (x / 120 + y / 46)) * (0.6 + 0.4 * Math.sin(TAU * (x / 80 - y / 90)));
    if (v * v * 0.7 > bayer(x, y) + hash(x, y, 62) * 0.25) p.set(x, y, v > 0.8 ? "#2a3e22" : "#1c2c1a");
  }
  return p.done();
}
function drawGrass(t) {
  const [sc, sx] = GRASS_SCR, off = scroll(t, 6, W), br = breezeAt(t);
  sx.globalCompositeOperation = "copy"; sx.drawImage(GRASS_TEX, off - W, 0); sx.globalCompositeOperation = "source-over"; sx.drawImage(GRASS_TEX, off, 0);
  sx.globalCompositeOperation = "destination-in"; sx.drawImage(LAWN_MASK, 0, 0); sx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 0.55 + br * 0.35; ctx.drawImage(sc, 0, GRASS_Y); ctx.globalAlpha = 1;
}

/* ================= The city's own rain: the last shower, and the drips ================= */
const RAIN = [
  { count: 150, nMin: 150, nMax: 180, fall: 190, ff: 1, len: 2, slope: 0.14, col: 'rgba(150,146,186,0.3)', warm: 'rgba(255,200,130,0.45)', land: a => 158 + Math.floor(a * 38) },
  { count: 100, nMin: 190, nMax: 220, fall: 230, ff: 0.9, len: 3, slope: 0.18, col: 'rgba(172,170,208,0.38)', warm: 'rgba(255,205,140,0.6)', land: a => CURB + 3 + Math.floor(a * 14) },
  { count: 46, nMin: 240, nMax: 270, fall: 290, ff: 0.82, len: 5, slope: 0.24, col: 'rgba(200,200,232,0.5)', warm: 'rgba(255,214,150,0.75)', land: a => 236 + Math.floor(a * 40) },
];
RAIN.forEach((L, li) => { L.drops = Array.from({ length: L.count }, (_, i) => ({ id: li * 1000 + i, n: L.nMin + Math.floor(hash(i, li, 861) * (L.nMax - L.nMin)), ph: hash(i, li, 862) })); });
const litAt = (x, y, lights) => lights.some(l => ((x - l.x) / l.rx) ** 2 + ((y - l.y) / l.ry) ** 2 < 1);
function drawRain(L, t, amt, lights) {
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    if (hash(d.id, cyc, 14) > amt) continue;
    const xl = -24 + hash(d.id, cyc, 11) * (W + 24 + L.fall * L.slope), yl = L.land(hash(d.id, cyc, 12));
    if (u < L.ff) {
      const s = u / L.ff, yh = yl - L.fall * (1 - s), xh = xl - (yl - yh) * L.slope;
      if (yh < -1) continue;
      ctx.fillStyle = litAt(xh, yh, lights) ? L.warm : L.col;
      for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh - k * L.slope), Math.round(yh - k), 1, 1);
    } else if (L.len > 2) {
      const s = (u - L.ff) / (1 - L.ff), sp = 1 + s * 1.5;
      ctx.globalAlpha = 1 - s; ctx.fillStyle = '#b8b8d8';
      ctx.fillRect(Math.round(xl - sp), Math.round(yl - 1 - Math.sin(Math.PI * s) * 1.5), 1, 1); ctx.fillRect(Math.round(xl + sp), Math.round(yl - 1 - Math.sin(Math.PI * s) * 1.5), 1, 1);
      ctx.globalAlpha = 1;
    }
  }
}
function drawDrips(t) {                                                                // drops falling off the near willow's strands
  for (let i = 0; i < 18; i++) {
    const s = STRANDS[(i * 7) % STRANDS.length], { cyc, u } = cycle(t, 60 + i * 4, hash(i, 3, 871));
    if (hash(i, cyc, 872) > 0.55) continue;
    const y = s.y0 + s.len + u * 30;
    if (y > 200) continue;
    ctx.globalAlpha = 0.7 * (1 - u); dot(s.x + Math.round(swayAt(t, s.ph) * 1.4), y, '#c8d0e8'); ctx.globalAlpha = 1;
  }
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the cart's sign; billboard: false leaves the sign out (the loop
// videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out; crowd: the share of the
// people to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = wxc.lights;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  OWN = !weather || weather === 'city' || !WEATHERS.includes(weather);
  WET_PICK = weather === 'drizzle' || weather === 'rain' || weather === 'thunderstorm';
  const rainy = WET_PICK, amt = wx.rain ? (weather === 'rain' ? 1 : showerAt(t)) : 0;
  drawSky(t);
  drawFar(t, weather);
  drawFarWalkers(t, crowd, rainy);
  if (wx.rain && amt > 0.01) drawRain(RAIN[0], t, amt, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawWater(t);
  drawBridge(t, crowd, rainy);
  drawMist(t);
  drawBats(t);
  drawLanding(t);
  drawSwans(t);
  drawMallards(t);
  drawDucklings(t);
  if (wx.petals) drawPetals(t, false);
  blit(GROUND_C);
  drawGrass(t);
  drawTulips(t);
  drawPuddles(t);
  if (wx.rain && amt > 0.01) drawRain(RAIN[1], t, amt, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawBank(t, crowd, pitch, billboard, rainy);
  if (OWN || WET_PICK) drawDrips(t);
  if (wx.rain && amt > 0.01) drawRain(RAIN[2], t, amt, lights);
  if (wx.petals) drawPetals(t, true);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const LAMP_TIP = Math.min(...[0, 1].map(k => brAt(PIER_C[k], BR.tower, BR.lampTop + 0.5)[1]));
const BOAT_LEFT = Math.round(pr(BOATS[2].lx, BOATS[2].ly, 0)[0] - 28);
const T0 = brAt(PIER_C[0], BR.tower, BR.lampTop + 0.5), T0B = brAt(PIER_C[0], BR.tower, 0);
export default {
  id: 'boston-public-garden',
  name: 'Boston',
  tagline: 'Swan boats after the rain',
  timeZone: 'America/New_York',
  accent: '#44bb11',                                   // the Green Line's green, written out: tools/seo/seo.mjs reads it from the source
  focusX: 300,
  landmark: [BOAT_LEFT, Math.round(brAt(BR_LEN, BR.half, 0)[0]) + 4],   // the Swan Boats at their landing and the footbridge
  landmarkTop: Math.round(LAMP_TIP) - 2,               // the bridge towers' globe lamps
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  spots: [
    { kind: 'landmark', x: Math.round(T0[0]) - 6, y: Math.round(T0[1]) - 2, w: 14, h: Math.round(T0B[1] - T0[1]) + 3 },
    { kind: 'window', x: PAV.x0 + 11, y: PAV.eave + 1, w: 11, h: 8 },
    { kind: 'water', x: 70, y: 172, w: 110, h: 18 },
  ],
  ambience: { water: 0.22, rain: 0.1, wind: 0.14, traffic: 0.16, crickets: 0.16, crowd: 0.03 },
  events: [
    { t: 12.9, type: 'splash', x: 150, gain: 0.05 },              // frogs plopping off the far edge (drawRings)
    { t: HERON[0] + 0.7, type: 'splash', x: HERON_AT.x, gain: 0.1 },
    { t: 59.1, type: 'splash', x: 196, gain: 0.05 },
    { t: HERON[1] + 0.7, type: 'splash', x: HERON_AT.x, gain: 0.1 },
    ...BELLS.chime.map(t => ({ t, type: 'chime', x: STEEPLE.x, gain: 0.26 })),
    ...STRIKES.map(t => ({ t, type: 'bell', x: STEEPLE.x, gain: 0.5 })),
    { t: HERON[2] + 0.7, type: 'splash', x: HERON_AT.x, gain: 0.1 },
    { t: 197.5, type: 'splash', x: 150, gain: 0.05 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
